import TransportOrderRoutePointsSection from './TransportOrderRoutePointsSection';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatMoneyPl } from '../utils/currency';
import { formatLocalizedNumber, formatLocalizedPercent } from '../utils/numberFormatting';
import { useFeedback } from '../utils/feedback';
import {
  Alert,
  App,
  Button,
  Form,
  Input,
  Modal,
  Space,
  Tag,
  Typography,
} from 'antd';
import { useCustomerOptions } from '../modules/reference-data/queries/useCustomerOptions';
import { useTransportCompanyOptions } from '../modules/transport-companies/queries/useTransportCompanyOptions';
import type { ColumnsType } from 'antd/es/table';
import {
  AimOutlined,
  CloseOutlined,
  EditOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { OrderOptionDto } from '../dto/order';
import type { SupplierOrderDto } from '../dto/supplierOrder';
import type { TransportRouteDetailDto } from '../dto/transport';
import { fetchOrderTransports } from '../modules/orders/api/orderTransportApi';
import { fetchCustomerOrderTruckMarks, type CustomerOrderTruckMarks } from '../modules/transports/api/transportRoutesApi';
import { isClosedOrderStatus } from '../modules/transports/closedOrders';
import TransportOrdersCargoSection from './TransportOrdersCargoSection';
import TransportFormSection, {
  TransportFormSectionsProvider,
  useTransportFormSections,
} from './TransportFormSection';
import TransportSupplierOrdersField, { TransportSelectedSupplierOrdersList } from './TransportSupplierOrdersField';
import TransportSupplierCargoEditor, { type SupplierCargoGroup } from './TransportSupplierCargoEditor';
import TransportRouteSection from './TransportRouteSection';
import TransportCarrierSection from './TransportCarrierSection';
import TransportCostsSection from './TransportCostsSection';
import TransportVehicleNotesSection from './TransportVehicleNotesSection';
import TransportKindPicker from './TransportKindPicker';
import TransportCostSplitPreview from './TransportCostSplitPreview';
import {
  deriveTransportKind,
  kindCarriesCustomerOrders,
  kindCarriesSupplierOrders,
  transportKindDirection,
  type TransportDirection,
  type TransportKind,
} from '../modules/transports/transportDirection';
import {
  buildInboundCostSplitPreview,
  allocateLargestRemainder,
  defaultKeyLabel as defaultKeyLabelFor,
  manualSplitDifference,
  resolveSavedPreviewRate,
  type InboundPreviewLine,
} from '../modules/transports/transportCostSplitPreview';
import { useTransportPreviewRate } from '../modules/transports/useTransportPreviewRate';
import { resolveSupplierOrderLoadingAddress } from '../modules/transports/transportKindAddresses';
import TransportStickySummary, {
  TRANSPORT_STICKY_OFFSET_BOTTOM,
  TRANSPORT_STICKY_SCROLL_CLEARANCE,
} from './TransportStickySummary';
import TransportDeliveryQuantityFields, {
  buildTransportDeliveryFormItems,
  type TransportDeliveryFormItem,
} from '../modules/transports/components/TransportDeliveryQuantityFields';
import {
  focusTransportRequiredField,
  TRANSPORT_KIND_PICKER_ANCHOR_ID,
  TRANSPORT_ORDER_SELECTION_ANCHOR_ID,
  sectionForTransportRequiredField,
  useTransportRequiredFields,
} from './transportRequiredFields';
import {
  buildCargoKey,
  buildCargoQuantitySnapshotFields,
  buildCargoRows,
  buildCargoRowsMetricsKey,
  getTransportOrderProductDisplayName,
  type CargoItemFormValue,
} from './sharedTransportFormRows';
import {
  type AllocationHintMap,
  buildAllocationPreview,
  buildSharedTransportFormValues,
  buildSharedTransportPayload,
  calculateCargoItemsMetrics,
  computeAllocationHints,
  DEFAULT_TRANSPORT_CURRENCY,
  deriveCustomerFromOrders,
  deriveDeliveryAddressesFromOrders,
  deriveLoadingAddressesFromOrders,
  getEffectiveTransportCargoItems,
  hasMissingCargoDimensions,
  isTransportGoodsValueKnown,
  planSavedCustomerOrderUnlink,
  shouldPreventEnterFormSubmit,
  type SavedOrderShare,
  type SharedTransportFormValues,
  type SharedTransportPreviewRow,
} from './sharedTransportFormUtils';
import { formatCargoQuantityTotals } from '../utils/cargoQuantity';
import {
  calculateTransportCostAllocation,
  commercialAllocationWarningKey,
  DEFAULT_ALLOCATION_METHOD,
  resolveAllocationKeyAvailability,
  roundMoney,
  TRAILER_PAYLOAD_KG,
  type AllocationKeyPosition,
  type AllocationMethod as CostAllocationMethod,
  type TransportCostAllocationResult,
  type TransportCostAllocationWarning,
} from '../utils/transportCostAllocation';
import {
  applySupplierCargoEntry,
  buildSupplierCargoPayload,
  excludeSupplierCargoLine,
  setValue,
  linesFromLoadItems,
  linesFromSupplierOrder,
  resolveSupplierCargoLines,
  supplierCargoPositions,
  supplierCargoFreightSet,
  supplierCargoWeight,
  type SupplierCargoEntry,
  type SupplierCargoUnit,
} from '../modules/transports/supplierTruckCargo';
import { getQuantitySetValue } from '../utils/commercialQuantity';
import { useSupplierCargoContext } from '../modules/transports/useSupplierCargoContext';
import { getSupplierOrderSummary, SupplierOrderStatusBadge } from '../modules/orders/components/SupplierOrderSummaryPreview';
import {
  deriveSupplierCargoEntries,
  keepUntouchedSupplierCargoLines,
  addGuessedQuantities,
  planSupplierCargoCap,
  guessUnpairedQuantities,
  pairedQuantityByLine,
  resolveCargoPairs,
  type CargoPair,
} from '../modules/transports/cargoPairing';
import TransportCargoPairingField from './TransportCargoPairingField';
import TransportGoodsDropBoard from '../modules/transports/components/TransportGoodsDropBoard';
import {
  buildDropBoard,
  buildLineRecipients,
  pickDropTarget,
  planDrop,
  unassignedMatches,
  type BoardStop,
  type BoardStopLine,
  type RecipientCandidate,
} from '../modules/transports/goodsRecipients';
import { supplierFreightShifts, type SupplierFreightShift } from '../modules/transports/supplierFreightShift';
import { formatLocalizedQuantity } from '../utils/numberFormatting';
import { formatUnit } from '../utils/units';

interface SharedTransportFormProps {
  availableOrders: OrderOptionDto[];
  /**
   * Etap 2 (transport przywozowy): supplier orders (ZD) available for the
   * inbound link. Omitted (or empty) hides nothing — the field still renders so
   * the operator can see the transport carries no inbound leg — but a page that
   * never loads them simply passes an empty list.
   */
  availableSupplierOrders?: SupplierOrderDto[];
  supplierOrdersLoading?: boolean;
  supplierOrdersLoadError?: boolean;
  onRetrySupplierOrders?: () => void;
  transport?: TransportRouteDetailDto | null;
  initialOrderIds?: string[];
  /**
   * Punkt startu tworzenia z kontekstu zamówienia (decyzja 2026-09-14):
   * wstępnie wybiera to zamówienie i pokazuje kartę „Transport dla
   * zamówienia" jako informację — NIE ogranicza formularza. Operator może
   * dołożyć kolejne zamówienia (picker klientów zawsze pełny), a guard ZD
   * wymaga powiązania z co najmniej jednym wybranym zamówieniem.
   */
  orderScopeId?: string;
  /**
   * Decision 2026-10-04: preset of the create form from a ZD/ZK card —
   * the truck kind (`?kind=`) and the ZD to carry (`?supplierOrderId=`).
   * Without a kind the form starts with the kind choice only.
   */
  initialKind?: TransportKind | null;
  initialSupplierOrderIds?: string[];
  /** Our warehouse (GET /transport-routes/defaults): fixed loading/unloading place. */
  warehouseAddress?: string | null;
  canEditTransportCosts?: boolean;
  /** Goods value is a customer sale value: hidden, not 0, without `canViewCustomerSalePrices`. */
  canViewGoodsValue?: boolean;
  canViewTransportCosts?: boolean;
  loading?: boolean;
  onCancel?: () => void;
  onSubmit: (payload: Record<string, unknown>) => Promise<void>;
}

/**
 * Item-level allocation under the goods value key. The frontend never
 * computes value shares (OPERATIONS has no prices): the lines carry the saved
 * server split (edit mode, saved key = value) or nothing until the save.
 */
function buildValueKeyAllocation(
  rows: Array<{ id?: string; key: string; goodsValueTotal?: number | null }>,
  transportCost: number,
  saved: TransportCostAllocationResult | null,
): TransportCostAllocationResult {
  const savedById = new Map((saved?.items || []).map((item) => [item.id, item]));
  const items = rows.flatMap((row) => {
    const item = savedById.get(row.id || row.key);
    return item ? [item] : [];
  });
  const totalGoodsValue = roundMoney(rows.reduce((sum, row) => sum + (Number(row.goodsValueTotal) || 0), 0));
  return {
    method: 'BY_VALUE',
    items,
    summary: {
      totalGoodsValue,
      totalAllocatedTransportCost: roundMoney(transportCost),
      totalLandedCost: roundMoney(totalGoodsValue + transportCost),
      allocationDifference: 0,
    },
    warnings: [],
  };
}

const SharedTransportFormInner: React.FC<SharedTransportFormProps> = ({
  availableOrders,
  availableSupplierOrders = [],
  supplierOrdersLoading = false,
  supplierOrdersLoadError = false,
  onRetrySupplierOrders,
  transport,
  initialOrderIds,
  orderScopeId,
  initialKind = null,
  initialSupplierOrderIds,
  warehouseAddress = null,
  canEditTransportCosts = true,
  canViewGoodsValue = true,
  canViewTransportCosts = true,
  loading = false,
  onCancel,
  onSubmit,
}) => {
  const { t, i18n } = useTranslation(['transport', 'common', 'orders', 'products']);
  const feedback = useFeedback();
  const { modal } = App.useApp();
  const [form] = Form.useForm<SharedTransportFormValues>();
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);
  // Etap 2 (transport przywozowy): supplier orders (ZD) carried inbound. Kept
  // in component state next to selectedOrderIds — the two are peers feeding the
  // same payload builder and the same "at least one link" invariant.
  const [selectedSupplierOrderIds, setSelectedSupplierOrderIds] = useState<string[]>([]);
  const [allocationHints, setAllocationHints] = useState<AllocationHintMap>({});
  const [allocationHintsLoading, setAllocationHintsLoading] = useState(false);
  const [allocationHintsError, setAllocationHintsError] = useState(false);
  // Prefill/auto-fill guards: once the operator edits customer, loading
  // addresses, or delivery addresses, we stop overwriting their input from the
  // selected orders.
  const [customerTouched, setCustomerTouched] = useState(false);
  const [loadingTouched, setLoadingTouched] = useState(false);
  const [deliveryTouched, setDeliveryTouched] = useState(false);
  // Usability round 2026-10-05: the planned delivery date follows the loading
  // date on a new truck until the operator picks one.
  const [estimatedDeliveryTouched, setEstimatedDeliveryTouched] = useState(false);
  const watchedLoadingDate = Form.useWatch('loadingDate', form);
  const [quantityTouchedKeys, setQuantityTouchedKeys] = useState<Set<string>>(new Set());
  const [excludedCargoKeys, setExcludedCargoKeys] = useState<Set<string>>(new Set());
  // Owner 2026-10-12 (edge run A): a ZK put on a factory truck from „Gdzie
  // jedzie” starts each line at what this truck brings for it (0 for lines
  // nothing was dropped on), not at the whole order remainder. The drop writes
  // the rows and marks them touched; this record remembers which rows a drop
  // wrote, so taking the ZK off the truck gives them back their defaults.
  // A dropped row the operator has not retyped also follows the ZD side
  // (capUntouchedRowsToSupplierCargo); `autoCargoQuantitiesRef` = what that
  // rule last wrote into it, so the row stays automatic.
  const [dropQuantities, setDropQuantities] = useState<Record<string, number>>({});
  const autoCargoQuantitiesRef = useRef<Record<string, number>>({});
  // NZ.5 (owner 2026-10-22): ZK rows the rule gave less because other
  // customers took the goods first (row key → what they would get alone);
  // „Gdzie jedzie” says so, nothing else.
  const [cargoReducedByOthers, setCargoReducedByOthers] = useState<Record<string, number>>({});
  // Review 2026-10-12: a drop on a customer not on the truck waits for that
  // ZK's coverage on other trucks; until its quantities are written the board
  // is busy and ignores further drops (no stale selection / board / kind).
  const [pendingDrop, setPendingDrop] = useState<{ lineId: string; orderId: string; targetKey: string; need: number | null } | null>(null);
  const [dropBusy, setDropBusy] = useState(false);
  const dropBusyRef = useRef(false);
  // Tomek 2026-10-21: a new truck puts the source ZK of a generated ZD on once.
  const sourceRecipientDoneRef = useRef(false);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  // Addendum A: how much of each ZD line THIS truck carries. Only lines the
  // operator touched are here; `supplierCargoTouched` is the touched guard of
  // the whole list (untouched = the key is omitted and the server keeps the
  // stored rows).
  const [supplierCargoEntries, setSupplierCargoEntries] = useState<Record<string, SupplierCargoEntry>>({});
  const [supplierCargoTouched, setSupplierCargoTouched] = useState(false);
  // PR3 (decision 2026-10-06): „z pozycji ZD” chosen by the operator per ZK
  // cargo row key (null = cleared); unset rows use the saved pair, else the
  // only match. `cargoEdited`: the operator changed the load in this session
  // (an edit-mode truck re-derives its ZD default quantities only then).
  const [cargoPairChoices, setCargoPairChoices] = useState<Record<string, string | null>>({});
  const [cargoEdited, setCargoEdited] = useState(false);
  const [cargoModalOpen, setCargoModalOpen] = useState(false);
  const [supplierCargoModalOpen, setSupplierCargoModalOpen] = useState(false);
  const [extraStopIds, setExtraStopIds] = useState<string[]>([]);
  // Decision 2026-10-04: rodzaj auta — the first choice; it sets the
  // direction (who pays) and which orders the form asks for. Edit mode
  // derives it from the saved truck.
  const [kind, setKind] = useState<TransportKind | null>(null);
  const [showUnitCosts, setShowUnitCosts] = useState(false);
  const [stickySummaryHeight, setStickySummaryHeight] = useState(0);
  const { options: customerOptions } = useCustomerOptions();
  const { options: transportCompanyOptions } = useTransportCompanyOptions();

  const isEditMode = Boolean(transport);
  // WP14 allowed a factory (INBOUND) truck without ZD; such a saved truck
  // keeps saving with a warning instead of the new-truck block.
  const savedWithoutSupplierOrders = Boolean(transport) && (transport?.supplierOrders?.length || 0) === 0;

  // Seed once per distinct input: a refetch that hands over an equal truck (a
  // new object, same content) must not reset the form over the operator's edits.
  const seededInputRef = useRef<string | null>(null);
  useEffect(() => {
    const seedInput = JSON.stringify([transport ?? null, initialOrderIds ?? null, orderScopeId ?? null, initialKind, initialSupplierOrderIds ?? null]);
    if (seededInputRef.current === seedInput) return;
    seededInputRef.current = seedInput;
    form.resetFields();
    form.setFieldsValue(buildSharedTransportFormValues(transport));
    // Review PR #267: a preset kind that carries no ZK / no ZD (a hand-typed
    // `?orderId=&kind=FACTORY_TO_WAREHOUSE`) does not seed those orders.
    const presetTakesOrders = !initialKind || kindCarriesCustomerOrders(initialKind);
    const presetTakesSupplierOrders = !initialKind || kindCarriesSupplierOrders(initialKind);
    const nextSelectedIds = Array.from(new Set([
      ...(transport?.routeOrders.map((routeOrder) => routeOrder.orderId) || []),
      ...(transport || presetTakesOrders ? (initialOrderIds || []) : []),
      // Decyzja 2026-09-14: tworzenie z kontekstu zamówienia wstępnie wybiera
      // (nie ogranicza). orderScopeId dokłada się do selekcji jak initialOrderIds,
      // więc operator może dołożyć kolejne zamówienia do tej ciężarówki.
      ...(orderScopeId && (transport || presetTakesOrders) ? [orderScopeId] : []),
    ]));
    setSelectedOrderIds(nextSelectedIds);
    // Etap 2 + decision 2026-10-04: the loaded transport's ZD, or the
    // create-mode preset from the ZD card (`?supplierOrderId=`).
    const nextSupplierOrderIds = Array.from(new Set([
      ...(transport?.supplierOrders || []).map((supplierOrder) => supplierOrder.id),
      ...(transport || !presetTakesSupplierOrders ? [] : (initialSupplierOrderIds || [])),
    ]));
    setSelectedSupplierOrderIds(nextSupplierOrderIds);
    const nextKind: TransportKind | null = transport
      ? deriveTransportKind({
        direction: transport.direction,
        customerOrderCount: transport.routeOrders.length,
        supplierOrderCount: transport.supplierOrders?.length || 0,
      })
      : initialKind
        || (nextSupplierOrderIds.length > 0 ? 'FACTORY_TO_WAREHOUSE' : null)
        || (nextSelectedIds.length > 0 ? 'WAREHOUSE_TO_CUSTOMER' : null);
    setKind(nextKind);
    if (nextKind) form.setFieldValue('direction', transportKindDirection(nextKind));
    setExcludedCargoKeys(new Set());
    setAllocationHints({});
    setAllocationHintsLoading(false);
    setAllocationHintsError(false);
    setQuantityTouchedKeys(new Set());
    // Edit mode loads stored values, so all fields count as user-owned.
    setCustomerTouched(Boolean(transport));
    setLoadingTouched(Boolean(transport));
    setDeliveryTouched(Boolean(transport));
    setEstimatedDeliveryTouched(Boolean(transport));
    setSupplierCargoEntries({});
    setSupplierCargoTouched(false);
    setCargoPairChoices({});
    setCargoEdited(false);
    setShowUnitCosts(false);
    setDropQuantities({});
    autoCargoQuantitiesRef.current = {};
    setPendingDrop(null);
    setDropBusy(false);
    dropBusyRef.current = false;
    sourceRecipientDoneRef.current = false;
  }, [form, transport, initialOrderIds, orderScopeId, initialKind, initialSupplierOrderIds]);

  // Fetch "already allocated elsewhere" per selected order so the cargo editor
  // can show remaining quantities. The subtraction of quantities already on the
  // current transport (when editing) is encapsulated in `computeAllocationHints`
  // so the invariant lives in one place.
  useEffect(() => {
    if (selectedOrderIds.length === 0) {
      setAllocationHints({});
      setAllocationHintsLoading(false);
      setAllocationHintsError(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setAllocationHintsLoading(true);
      setAllocationHintsError(false);
      const results = await Promise.all(
        selectedOrderIds.map(async (orderId) => {
          try {
            return { data: await fetchOrderTransports(orderId), failed: false };
          } catch (error) {
            return {
              data: { transports: [], productRealization: [] },
              failed: true,
            };
          }
        }),
      );

      if (cancelled) return;
      setAllocationHints(computeAllocationHints(results.map((result) => result.data), transport));
      setAllocationHintsError(results.some((result) => result.failed));
      setAllocationHintsLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedOrderIds, transport]);

  const selectedOrders = availableOrders.filter((order) => selectedOrderIds.includes(order.id));
  const selectedSupplierOrders = availableSupplierOrders
    .filter((supplierOrder) => selectedSupplierOrderIds.includes(supplierOrder.id));
  const scopedOrder = orderScopeId
    ? availableOrders.find((order) => order.id === orderScopeId)
    : undefined;
  const cargoItemsByKey = (Form.useWatch('cargoItemsByKey', form) || {}) as Record<string, CargoItemFormValue>;
  const currency = Form.useWatch('currency', form) as string | undefined;
  const status = Form.useWatch('status', form) as string | undefined;
  const effectiveTransportCargoItems = getEffectiveTransportCargoItems(transport);
  const allCargoRows = buildCargoRows(
    selectedOrders,
    cargoItemsByKey,
    allocationHints,
    effectiveTransportCargoItems,
    t('products:catalogPicker.basicProduct'),
  );
  const cargoRows = allCargoRows.filter((row) => !excludedCargoKeys.has(row.key));
  const missingRequiredFields = useTransportRequiredFields({
    form,
    selectedOrderIds,
    // Etap 2: an inbound-only transport satisfies the "orders" requirement
    // through its supplier orders, so the checklist must see both sides.
    selectedSupplierOrderIds,
    cargoRows,
    // Goods value is a customer sale value: not required from roles that cannot see it.
    canEditTransportCosts: canEditTransportCosts && canViewGoodsValue,
    transportKind: kind,
    supplierOrderOptional: savedWithoutSupplierOrders,
    routeOrders: selectedOrders.map((order) => ({ id: order.id, orderNumber: order.displayOrderNumber || order.orderNumber })),
  });
  // Sections start open (decision 2026-10-04); a section the operator closed
  // is expanded again first, then focused once the panel has laid out.
  const sections = useTransportFormSections();
  const focusRequiredField = useCallback(
    (field: Parameters<typeof focusTransportRequiredField>[1]) => {
      sections?.openSection(sectionForTransportRequiredField(field));
      window.setTimeout(() => focusTransportRequiredField(form, field), sections ? 250 : 0);
    },
    [form, sections],
  );
  const selectedCargoItems = cargoRows
    .filter((row) => Number(row.quantity || 0) > 0)
    .map(({
      key,
      orderNumber,
      availableQuantity,
      allocatedElsewhere,
      productDimensions,
      productVariantLabels,
      productVariantId: _productVariantId,
      orderLineNumber: _orderLineNumber,
      ...item
    }) => item);
  const deliveryCargoKey = JSON.stringify(selectedCargoItems.map((item) => ({
    orderProductId: item.orderProductId,
    quantity: item.quantity,
    unit: item.unit,
    quantityPcs: item.quantityPcs,
    quantityM2: item.quantityM2,
    quantityM3: item.quantityM3,
  })));

  useEffect(() => {
    if (status !== 'DELIVERED') {
      form.setFieldValue('deliveryItems', []);
      return;
    }

    const currentDeliveryItems = (form.getFieldValue('deliveryItems') || []) as Array<{
      orderProductId?: string;
      deliveredQuantity?: number;
      shortfall?: TransportDeliveryFormItem['shortfall'];
    }>;
    const currentByProductId = new Map(currentDeliveryItems.map((item) => [item.orderProductId, item]));
    const nextItems = buildTransportDeliveryFormItems(selectedCargoItems).map((item) => {
      const current = currentByProductId.get(item.orderProductId);
      return current
        ? { ...item, deliveredQuantity: current.deliveredQuantity as number, shortfall: current.shortfall ?? item.shortfall }
        : item;
    });
    form.setFieldValue('deliveryItems', nextItems);
    // selectedCargoItems is represented by the stable identity/quantity key.
    // Re-running for a cost-only render would overwrite in-progress delivery
    // confirmation input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryCargoKey, form, status]);
  const orderIdsWithCargo = new Set(selectedCargoItems.map((item) => item.orderId));
  const missingCargoOrderNumbers = selectedOrders
    .filter((order) => !orderIdsWithCargo.has(order.id))
    .map((order) => order.displayOrderNumber || order.orderNumber);
  const hasMissingCustomerCargo = missingCargoOrderNumbers.length > 0;
  const costAllocationMethod = (
    Form.useWatch('costAllocationMethod', form) as CostAllocationMethod | undefined
  ) || DEFAULT_ALLOCATION_METHOD;
  const transportRate = Form.useWatch('transportRate', form) as number | null | undefined;
  const direction = Form.useWatch('direction', form) as TransportDirection | undefined;
  const multiOrder = selectedOrders.length >= 2;
  // WP14: an INBOUND truck charges no customer order (its freight goes into
  // the ZD batches), so the per-order split preview does not apply.
  const showCustomerSplit = multiOrder && direction !== 'INBOUND';
  const transportCostForAllocation = canEditTransportCosts
    ? Number(transportRate || 0)
    : Number(transport?.transportRate ?? transport?.transportCost ?? 0);
  // Rows with quantity 0 mean "not loaded on this transport" and are excluded
  // from the payload, so they must not participate in the cost allocation.
  const loadedCargoRows = cargoRows.filter((row) => Number(row.quantity || 0) > 0);
  const valueKey = costAllocationMethod === 'BY_VALUE';
  // Goods value key: the split is the server's (OPERATIONS has no prices).
  // Edit mode shows the saved per-line shares while the saved key is value;
  // otherwise the lines read "—" until the save computes them.
  const savedValueAllocation = valueKey && transport?.costAllocationMethod === 'BY_VALUE'
    ? transport.costAllocation ?? null
    : null;
  const transportCostAllocation: TransportCostAllocationResult = valueKey
    ? buildValueKeyAllocation(loadedCargoRows, transportCostForAllocation, savedValueAllocation)
    : calculateTransportCostAllocation({
      transportCost: transportCostForAllocation,
      method: costAllocationMethod,
      items: loadedCargoRows.map((row) => ({
        id: row.id || row.key,
        quantity: row.quantity,
        unit: row.unit,
        quantityPcs: row.quantityPcs,
        goodsValueTotal: row.goodsValueTotal,
        manualAllocatedTransportCost: row.manualAllocatedTransportCost,
        // Product identity + dimensions (mm) feed the m² / m³ keys in the
        // live preview; the saved payload is unchanged.
        name: row.productName,
        width: row.width,
        height: row.height,
        length: row.length,
        thickness: row.thickness,
        countInPiecesOnly: row.countInPiecesOnly === true,
      })),
    });
  const allocationItemsById = new Map(
    transportCostAllocation.items.map((item) => [item.id, item]),
  );
  // Sticky summary „Wartość towaru” / „Łączny koszt”: "—" unless the goods
  // value of every loaded line is known (rule: isTransportGoodsValueKnown).
  const goodsValueKnown = isTransportGoodsValueKnown({
    supplierOrderCount: selectedSupplierOrderIds.length,
    rows: loadedCargoRows.map((row) => ({
      goodsValueTotal: row.goodsValueTotal,
      savedGoodsValueTotal: row.id
        ? (transport?.cargoItems || []).find((item) => item.id === row.id)?.goodsValueTotal
        : undefined,
      saved: Boolean(row.id && (transport?.cargoItems || []).some((item) => item.id === row.id)),
      touched: (form.isFieldTouched as (name: Array<string | number>) => boolean)(['cargoItemsByKey', row.key, 'goodsValueTotal']),
    })),
  });
  // Saved per-order shares (PLN) of a value-key truck, shown in the preview.
  const savedValueShares: Record<string, SavedOrderShare> = transport?.costAllocationMethod === 'BY_VALUE'
    ? Object.fromEntries((transport.routeOrders || []).map((routeOrder) => [routeOrder.orderId, {
      percent: routeOrder.allocationPercent ?? null,
      cost: routeOrder.allocatedTransportCost ?? null,
    }]))
    : {};
  // Display-only preview: split the rate as entered, in the form's own
  // currency. No FX conversion — what is SAVED to the backend is untouched.
  const previewTransportRate = canEditTransportCosts ? Number(transportRate || 0) : 0;
  const { rows: allocationPreview, missingDimensionOrders } = buildAllocationPreview(
    selectedOrders,
    previewTransportRate,
    costAllocationMethod,
    selectedCargoItems,
    savedValueShares,
  );

  // Manual key (decision 2026-10-04; round 3, B3): switching to „ręcznie”
  // starts from exactly the per-ZK amounts the split table showed (the live
  // preview, or the server's saved shares for the value key), so only the
  // differences need typing. Each order's amount is spread over its lines by
  // quantity, summing to the shown amount to the grosz.
  const lastAutomaticOrderSplitRef = useRef<Map<string, number>>(new Map());
  if (costAllocationMethod !== 'MANUAL') {
    const shown = new Map<string, number>();
    // The value key shows the saved PLN shares; in another truck currency
    // the manual fields take the same share of the typed cost.
    const savedSharesInPLN = valueKey && (currency || DEFAULT_TRANSPORT_CURRENCY) !== 'PLN';
    allocationPreview.forEach((row) => {
      const amount = savedSharesInPLN
        ? (row.allocationPercent === null ? null : Math.round(row.allocationPercent * previewTransportRate) / 100)
        : row.allocatedTransportCost;
      if (amount !== null && amount !== undefined && Number.isFinite(Number(amount)) && Number(amount) > 0) {
        shown.set(row.orderId, Number(amount));
      }
    });
    lastAutomaticOrderSplitRef.current = shown;
  }
  const [manualPrefilled, setManualPrefilled] = useState(false);
  useEffect(() => {
    if (costAllocationMethod !== 'MANUAL' || direction === 'INBOUND') {
      setManualPrefilled(false);
      return;
    }
    const current = (form.getFieldValue('cargoItemsByKey') || {}) as Record<string, CargoItemFormValue>;
    // A truck saved with „Ręcznie” keeps its saved amounts as they are (an
    // empty one shows „zostało … do rozdzielenia”, it is not filled in).
    if (transport?.costAllocationMethod === 'MANUAL'
      && loadedCargoRows.some((row) => Number(current[row.key]?.manualAllocatedTransportCost) > 0)) return;
    const next = { ...current };
    let changed = false;
    // Usability round 2026-10-05: per order — an order whose amount is still
    // empty (e.g. just added with „Dołóż”) takes its share of the current
    // split; an amount the operator already has stays.
    lastAutomaticOrderSplitRef.current.forEach((amount, orderId) => {
      const rows = loadedCargoRows.filter((row) => row.orderId === orderId);
      if (rows.length === 0) return;
      if (rows.some((row) => Number(current[row.key]?.manualAllocatedTransportCost) > 0)) return;
      const amounts = allocateLargestRemainder(amount, rows.map((row) => Math.max(Number(row.quantity) || 0, 0)));
      rows.forEach((row, index) => {
        next[row.key] = { ...next[row.key], manualAllocatedTransportCost: amounts[index] };
      });
      changed = true;
    });
    if (changed) {
      form.setFieldsValue({ cargoItemsByKey: next });
      setManualPrefilled(true);
    }
    // Runs on the key switch only; later edits are the operator's.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [costAllocationMethod]);

  // Addendum A: the ZD lines this INBOUND truck carries (editor + payload).
  const isInbound = direction === 'INBOUND';
  const savedSupplierOrdersById = new Map(
    (transport?.supplierOrders || [])
      .filter((supplierOrder) => Array.isArray(supplierOrder.loadItems))
      .map((supplierOrder) => [supplierOrder.id, supplierOrder]),
  );
  // The rule per ZD needs what the OTHER trucks enter and which of them carry
  // the rest: the server's `cargoContext` (saved ZD: from the truck detail;
  // new ZD: one request per ZD).
  const savedSupplierCargoContexts = useMemo(() => Object.fromEntries(
    (transport?.supplierOrders || [])
      .filter((supplierOrder) => supplierOrder.cargoContext !== undefined)
      .map((supplierOrder) => [supplierOrder.id, supplierOrder.cargoContext]),
  ), [transport?.supplierOrders]);
  const supplierCargoContexts = useSupplierCargoContext(selectedSupplierOrderIds, {
    currentTransportId: transport?.id,
    enabled: isInbound,
    savedContexts: savedSupplierCargoContexts,
  });
  const supplierCargoGroups: SupplierCargoGroup[] = selectedSupplierOrderIds.flatMap((supplierOrderId) => {
    const saved = savedSupplierOrdersById.get(supplierOrderId);
    const context = supplierCargoContexts[supplierOrderId];
    const candidate = saved
      ? null
      : availableSupplierOrders.find((supplierOrder) => supplierOrder.id === supplierOrderId);
    if (!saved && !candidate) return [];
    const source = candidate ?? availableSupplierOrders.find((supplierOrder) => supplierOrder.id === supplierOrderId);
    return [{
      supplierOrderId,
      // Owner 2026-10-10: the ZD header says whose goods and where they load.
      supplierName: getSupplierOrderSummary((saved ?? candidate)!).supplierName,
      loadingPlace: resolveSupplierOrderLoadingAddress(source) || null,
      statusTag: source?.status ? <SupplierOrderStatusBadge status={source.status} /> : null,
      label: saved
        ? (saved.displayOrderNumber || saved.orderNumber)
        : getSupplierOrderSummary(candidate!).displayNumber,
      lines: saved ? linesFromLoadItems(saved, context) : linesFromSupplierOrder(candidate!, context ?? null),
      otherTruckLabels: context?.restTruckLabels || [],
      contextUnavailable: context === null,
      loading: context === undefined,
    }];
  });
  const supplierCargoLines = supplierCargoGroups.flatMap((group) => group.lines);
  const generatedSupplierOrderIds = new Set([...availableSupplierOrders, ...(transport?.supplierOrders || [])]
    .filter((order) => order.draftSourceOrderId && ['PENDING', 'REVIEWED'].includes(order.draftReviewStatus || ''))
    .map((order) => order.id));
  const directDeliveryLines = supplierCargoLines.filter((line) => generatedSupplierOrderIds.has(line.supplierOrderId));
  const directDeliveryLineIds = new Set(directDeliveryLines.map((line) => line.itemId));
  const onlyWarehousePurchases = selectedSupplierOrderIds.length > 0
    && selectedSupplierOrderIds.every((id) => !generatedSupplierOrderIds.has(id));


  // PR3 (decision 2026-10-06): on „Fabryka → klient” every ZK cargo line comes
  // from a ZD line on this truck, and an untyped ZD line carries Σ of its ZK
  // lines (rules: modules/transports/cargoPairing.ts).
  const pairingActive = kind === 'FACTORY_TO_CUSTOMER';
  const savedCargoPairs = useMemo(() => new Map((transport?.cargoItems || [])
    .filter((item) => item.orderProductId)
    .map((item) => [buildCargoKey(item.orderId, item.orderProductId as string), item.supplierOrderItemId ?? null])),
  [transport?.cargoItems]);
  const cargoPairs: Map<string, CargoPair> = pairingActive
    ? resolveCargoPairs(cargoRows, supplierCargoLines, cargoPairChoices, savedCargoPairs, directDeliveryLineIds)
    : new Map();
  // A ZK line without a pair still rides a ZD line (review 2026-10-07): the
  // default ZD quantities, the loading point and the freight split count it on
  // the line the card shows it on („do przypisania”); the pair itself stays unsaved.
  const unpairedGuess = pairingActive
    ? guessUnpairedQuantities(loadedCargoRows, cargoPairs, supplierCargoLines, supplierCargoEntries)
    : { byLine: new Map<string, number>(), linesByRow: new Map<string, string[]>() };
  const cargoLineIdsOf = (rowKey: string): string[] => {
    const paired = cargoPairs.get(rowKey)?.lineId;
    return paired ? [paired] : unpairedGuess.linesByRow.get(rowKey) ?? [];
  };
  // „Trasa każdego zamówienia” on a factory truck: a ZK loads where the ZD of
  // its paired line loads; without a pair, where the only ZD loads (review 2026-10-06).
  const supplierLoadingById = new Map(selectedSupplierOrders.map((supplierOrder) => [
    supplierOrder.id, resolveSupplierOrderLoadingAddress(supplierOrder),
  ]));
  const routePointOrders = selectedOrders.map((order) => {
    if (!kind || !kindCarriesSupplierOrders(kind)) return order;
    const pairedZdIds = cargoRows
      .filter((row) => row.orderId === order.id)
      .map((row) => supplierCargoLines.find((line) => line.itemId === cargoLineIdsOf(row.key)[0])?.supplierOrderId)
      .filter((id): id is string => Boolean(id));
    const zdId = pairedZdIds[0] ?? (selectedSupplierOrders.length === 1 ? selectedSupplierOrders[0].id : undefined);
    const hint = zdId ? supplierLoadingById.get(zdId) : '';
    return hint ? { ...order, factoryLocation: hint } : order;
  });
  // „Dla kogo jest ten towar?” (2026-10-09): every ZD line on a factory
  // truck names its customers; the same pairs as „z pozycji ZD”.
  const goodsRecipients = kind && kindCarriesSupplierOrders(kind)
    ? buildLineRecipients(
      directDeliveryLines,
      availableOrders,
      selectedOrderIds,
      new Map(Array.from(cargoPairs.entries()).map(([key, pair]) => [key, pair.lineId])),
      buildCargoKey,
    )
    : [];
  const derivesSupplierCargo = pairingActive && (!isEditMode || cargoEdited);
  const customerSumCargo = derivesSupplierCargo
    ? deriveSupplierCargoEntries(
      supplierCargoLines,
      addGuessedQuantities(pairedQuantityByLine(loadedCargoRows, cargoPairs, supplierCargoLines), unpairedGuess.byLine),
      {},
    )
    : {};
  // The other lines of a generated ZD on a customer sum keep what they carried (task 2026-10-14).
  const derivableSupplierCargo = {
    ...keepUntouchedSupplierCargoLines(supplierCargoLines, customerSumCargo, supplierCargoEntries, directDeliveryLineIds),
    ...customerSumCargo,
  };
  const derivedSupplierCargo = Object.fromEntries(Object.entries(derivableSupplierCargo)
    .filter(([itemId]) => !supplierCargoEntries[itemId]));
  const customerSumLineIds = new Set(Object.keys(derivedSupplierCargo).filter((itemId) => customerSumCargo[itemId]));
  const effectiveSupplierCargoEntries: Record<string, SupplierCargoEntry> = { ...derivedSupplierCargo, ...supplierCargoEntries };

  const supplierCargoLinesRef = useRef(supplierCargoLines);
  supplierCargoLinesRef.current = supplierCargoLines;
  const derivableSupplierCargoRef = useRef(derivableSupplierCargo);
  derivableSupplierCargoRef.current = derivableSupplierCargo;
  const customerSumCargoRef = useRef(customerSumCargo);
  customerSumCargoRef.current = customerSumCargo;
  const handleSupplierCargoChange = useCallback((itemId: string, entry: SupplierCargoEntry) => {
    setSupplierCargoTouched(true);
    setSupplierCargoEntries((previous) => {
      const derivable = derivableSupplierCargoRef.current;
      // „Przywróć” on a line with a customer sum: back to that sum.
      if (entry.quantity === null && derivable[itemId]) {
        const { [itemId]: _restored, ...rest } = previous;
        return rest;
      }
      const derived = Object.fromEntries(Object.entries(derivable).filter(([id]) => !previous[id]));
      const lines = supplierCargoLinesRef.current;
      const next = applySupplierCargoEntry(lines, { ...derived, ...previous }, itemId, entry);
      // Lines still on their customer sum stay derived (they follow the ZK);
      // the kept lines of the edited ZD stay at what they showed (rule 2026-10-05).
      const zdOf = (id: string) => lines.find((line) => line.itemId === id)?.supplierOrderId;
      const editedZd = zdOf(itemId);
      return Object.fromEntries(Object.entries(next)
        .filter(([id]) => id === itemId || Boolean(previous[id]) || !derived[id]
          || (!customerSumCargoRef.current[id] && zdOf(id) === editedZd)));
    });
  }, []);

  // Key availability (informational, greys out options): edit mode reads the
  // server's answer; create mode mirrors the rule without prices.
  const mirrorPositions: AllocationKeyPosition[] = isInbound
    ? supplierCargoPositions(supplierCargoLines, effectiveSupplierCargoEntries)
    : loadedCargoRows.map((row) => ({
      productName: row.productName,
      unit: row.unit,
      quantity: row.quantity,
      m2: row.quantityM2,
      m3: row.quantityM3,
      width: row.width,
      height: row.height,
      length: row.length,
      thickness: row.thickness,
      countInPiecesOnly: row.countInPiecesOnly === true,
      source: 'ZK' as const,
      cargoRow: true,
    }));
  const keyAvailability = transport?.allocationKeyAvailability
    || resolveAllocationKeyAvailability(mirrorPositions);

  // Create-mode auto-fill: keep customer in sync with the selected orders until
  // the operator types their own value. Clearing the selection resets it.
  useEffect(() => {
    if (isEditMode || customerTouched) {
      return;
    }

    form.setFieldValue('customer', deriveCustomerFromOrders(selectedOrders));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrderIds, isEditMode, customerTouched, availableOrders]);

  // Create-mode prefill: fill the loading-address blocks from the selected
  // orders' factory places until the operator edits the list. When no order
  // carries an address we leave the single empty block (same as before).
  useEffect(() => {
    if (isEditMode || loadingTouched) {
      return;
    }

    // Decision 2026-10-04: „Magazyn → klient” loads at our warehouse; a
    // factory truck at the ZD's factory/supplier (then the ZK factory place).
    const prefilled = kind === 'WAREHOUSE_TO_CUSTOMER'
      ? [warehouseAddress || ''].filter(Boolean)
      : Array.from(new Set([
        ...selectedSupplierOrders.map(resolveSupplierOrderLoadingAddress).filter(Boolean),
        ...deriveLoadingAddressesFromOrders(selectedOrders),
      ]));
    form.setFieldValue('loadingAddresses', prefilled.length > 0 ? prefilled : ['']);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrderIds, selectedSupplierOrderIds, isEditMode, loadingTouched, availableOrders, availableSupplierOrders, kind, warehouseAddress]);

  // Round 2 (U6): a new factory truck is priced in its ZD's currency (e.g.
  // EUR) until the operator picks one. Round 3 (B6, money): only while no
  // cost is typed — a typed amount never changes currency under it; the
  // costs section warns about the difference instead.
  const firstSupplierCurrency = selectedSupplierOrders.find((supplierOrder) => supplierOrder.currency)?.currency;
  useEffect(() => {
    if (isEditMode || !isInbound || !firstSupplierCurrency) return;
    if ((form.isFieldTouched as (name: string) => boolean)('currency')) return;
    const typedRate = form.getFieldValue('transportRate');
    if (typedRate !== null && typedRate !== undefined && typedRate !== '') return;
    if (['PLN', 'EUR', 'USD'].includes(firstSupplierCurrency) && form.getFieldValue('currency') !== firstSupplierCurrency) {
      form.setFieldValue('currency', firstSupplierCurrency);
    }
  }, [firstSupplierCurrency, form, isEditMode, isInbound]);

  useEffect(() => {
    if (isEditMode || estimatedDeliveryTouched) return;
    form.setFieldValue('estimatedDelivery', watchedLoadingDate ?? null);
  }, [estimatedDeliveryTouched, form, isEditMode, watchedLoadingDate]);

  // Create-mode prefill: fill the delivery-address blocks from the selected
  // orders' unload places until the operator edits the list. When no order
  // carries an address we leave the single empty block (same as before).
  useEffect(() => {
    if (isEditMode || deliveryTouched) {
      return;
    }

    // Decision 2026-10-04: „Fabryka → magazyn” unloads at our warehouse;
    // the other kinds at the customers of the selected ZK.
    const prefilled = kind === 'FACTORY_TO_WAREHOUSE'
      ? [warehouseAddress || ''].filter(Boolean)
      : deriveDeliveryAddressesFromOrders(selectedOrders);
    form.setFieldValue('deliveryAddresses', prefilled.length > 0 ? prefilled : ['']);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrderIds, isEditMode, deliveryTouched, availableOrders, kind, warehouseAddress]);

  // Round 3 (B5): taking a saved ZK off the truck also takes off the
  // unloading point only it went to (its saved route point). Points no order
  // is assigned to were typed by the operator and stay; the last one stays.
  // Same rule as the server's unlink (dropRemovedOrderDeliveryPoint).
  const previousOrderIdsRef = useRef<string[]>(selectedOrderIds);
  useEffect(() => {
    const previous = previousOrderIdsRef.current;
    previousOrderIdsRef.current = selectedOrderIds;
    if (!isEditMode || !transport) return;
    const removed = previous.filter((orderId) => !selectedOrderIds.includes(orderId));
    if (removed.length === 0) return;
    const savedPoint = (orderId: string) => (transport.routeOrders.find((row) => row.orderId === orderId)?.deliveryAddress || '').trim();
    const stillUsed = new Set([
      ...selectedOrderIds.map(savedPoint),
      ...deriveDeliveryAddressesFromOrders(selectedOrders).map((address) => address.trim()),
    ].filter(Boolean));
    const current = ((form.getFieldValue('deliveryAddresses') || []) as unknown[]).map((address) => String(address ?? ''));
    let next = current;
    removed.map(savedPoint).filter(Boolean).forEach((point) => {
      if (stillUsed.has(point) || next.filter((address) => address.trim()).length <= 1) return;
      next = next.filter((address) => address.trim() !== point);
    });
    if (next.length !== current.length) form.setFieldValue('deliveryAddresses', next.length > 0 ? next : ['']);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrderIds]);

  useEffect(() => {
    const previousItems = (form.getFieldValue('cargoItemsByKey') || {}) as Record<string, CargoItemFormValue>;
    const nextItems = { ...previousItems };
    let changed = false;
    const existingTransportOrderIds = new Set(
      transport?.routeOrders.map((routeOrder) => routeOrder.orderId) || []
    );
    const transportHasCargoItems = Boolean(transport && transport.cargoItems.length > 0);
    const currentTransportCargoItems = getEffectiveTransportCargoItems(transport);

    availableOrders
      .filter((order) => selectedOrderIds.includes(order.id))
      .forEach((order) => {
        order.products.forEach((product) => {
          const key = `${order.id}:${product.id}`;
          if (nextItems[key] !== undefined) {
            return;
          }

          const existingCargoItem = currentTransportCargoItems.find((item) =>
            item.orderId === order.id && item.orderProductId === product.id
          );
          const shouldDefaultMissingCargoToZero =
            transportHasCargoItems && existingTransportOrderIds.has(order.id);
          const quantitySnapshotFields = buildCargoQuantitySnapshotFields(
            product,
            existingCargoItem,
          );

          nextItems[key] = {
            id: existingCargoItem?.id,
            quantity: existingCargoItem?.quantity
              ?? (shouldDefaultMissingCargoToZero ? 0 : Math.max(Number(product.quantity || 0), 0)),
            unit: (existingCargoItem?.unit ?? product.unit ?? 'pcs') as CargoItemFormValue['unit'],
            ...quantitySnapshotFields,
            goodsValueTotal: existingCargoItem?.goodsValueTotal ?? 0,
            manualAllocatedTransportCost: existingCargoItem?.manualAllocatedTransportCost ?? null,
          };
          changed = true;
        });
      });

    if (changed) {
      form.setFieldsValue({ cargoItemsByKey: nextItems });
    }
  }, [availableOrders, form, selectedOrderIds, transport]);

  // Production 2026-10-22 (TR-ZK-2/09/2026-1): on a new „Fabryka → klient”
  // truck the untouched ZK rows also follow what the ZD side brings of their
  // variant (capUntouchedRowsToSupplierCargo), once the ZD lines are loaded.
  const capsRowsToSupplierCargo = pairingActive && !isEditMode
    && !supplierCargoGroups.some((group) => group.loading);
  const supplierCargoCapKey = capsRowsToSupplierCargo
    ? JSON.stringify([
      // Every input of the cap (review PR #431, M3): what each line carries and is free of.
      supplierCargoLines.map((line) => [line.itemId, line.productVariantId ?? null, customerSumLineIds.has(line.itemId),
        line.ordered, line.otherExplicit, line.otherRestTrucks]),
      effectiveSupplierCargoEntries,
      cargoRows.map((row) => [row.key, row.quantity, row.unit]),
    ])
    : '';

  // Every untouched line of a ZK the saved truck does not carry yet (all ZK in
  // create mode; in edit mode a ZK added through `?orderId=`, „Edytuj ładunek
  // i powiąż”) starts at the quantity still available after other active
  // transports — the same rest as `POST /:id/orders` (production ZD 10/43,
  // 2026-10-28: the edit form put a line carried by another truck on board in
  // full). Lines of the ZK already on the saved truck keep their stored
  // quantities. The realization request is asynchronous, so this second pass
  // corrects rows that were seeded before the response arrived. A quantity
  // the operator has already touched is never overwritten.
  const savedTruckOrderIdsKey = (transport?.routeOrders || []).map((routeOrder) => routeOrder.orderId).join(',');
  useEffect(() => {
    if (allocationHintsLoading || allocationHintsError) {
      // No plan now: no stale „Zmniejszono…” either (review PR #433, LOW-1).
      setCargoReducedByOthers((previous) => (Object.keys(previous).length > 0 ? {} : previous));
      return;
    }

    const previousItems = (form.getFieldValue('cargoItemsByKey') || {}) as Record<string, CargoItemFormValue>;
    const nextItems = { ...previousItems };
    let changed = false;

    const defaults = new Map<string, number>();
    const droppedKeys = new Set<string>();
    const savedTruckOrderIds = new Set(savedTruckOrderIdsKey ? savedTruckOrderIdsKey.split(',') : []);
    selectedOrders.forEach((order) => {
      if (savedTruckOrderIds.has(order.id)) return;
      order.products.forEach((product) => {
        const key = buildCargoKey(order.id, product.id);
        if (excludedCargoKeys.has(key) || !previousItems[key]) {
          return;
        }
        if (quantityTouchedKeys.has(key)) {
          // A drop wrote this row and nobody retyped it: it follows the ZD
          // side from what the drop gave it.
          const dropped = dropQuantities[key];
          const current = Number(previousItems[key].quantity || 0);
          if (capsRowsToSupplierCargo && dropped !== undefined
            && (current === dropped || current === autoCargoQuantitiesRef.current[key])) {
            defaults.set(key, dropped);
            droppedKeys.add(key);
          }
          return;
        }

        const hint = allocationHints[product.id];
        defaults.set(key, hint
          ? Math.max(Number(hint.remainingQuantity || 0), 0)
          : Math.max(Number(product.quantity || 0), 0));
      });
    });
    const plan = capsRowsToSupplierCargo
      ? planSupplierCargoCap(
        cargoRows.filter((row) => previousItems[row.key]),
        defaults,
        supplierCargoLines,
        new Map(Array.from(resolveSupplierCargoLines(supplierCargoLines, effectiveSupplierCargoEntries).entries())
          .map(([itemId, state]) => [itemId, state.carried])),
        customerSumLineIds,
        droppedKeys,
      )
      : null;
    const quantities = plan?.quantities ?? defaults;
    const reduced = Object.fromEntries(plan?.reducedByOthers ?? []);
    setCargoReducedByOthers((previous) => (JSON.stringify(previous) === JSON.stringify(reduced) ? previous : reduced));
    quantities.forEach((quantity, key) => {
      if (quantityTouchedKeys.has(key)) autoCargoQuantitiesRef.current[key] = quantity;
      if (Number(previousItems[key].quantity || 0) === quantity) {
        return;
      }

      nextItems[key] = { ...previousItems[key], quantity };
      changed = true;
    });

    if (changed) {
      form.setFieldsValue({ cargoItemsByKey: nextItems });
    }
    // The cap inputs are read as rendered; supplierCargoCapKey says when they change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allocationHints,
    allocationHintsError,
    allocationHintsLoading,
    dropQuantities,
    excludedCargoKeys,
    form,
    quantityTouchedKeys,
    savedTruckOrderIdsKey,
    selectedOrders,
    supplierCargoCapKey,
  ]);

  // Review 2026-10-12 + live run (FAIL 10): a ZK leaving the truck (✕ on the
  // board, „Co jedzie”, a kind switch) forgets everything this session set for
  // its lines — drop quantities, touched marks, chosen/cleared pairs, removed
  // rows and the form rows — so putting it back starts like the first time.
  const previousDropOrderIdsRef = useRef<string[]>(selectedOrderIds);
  useEffect(() => {
    const previous = previousDropOrderIdsRef.current;
    previousDropOrderIdsRef.current = selectedOrderIds;
    const removed = previous.filter((orderId) => !selectedOrderIds.includes(orderId));
    if (removed.length === 0) return;
    const ofRemoved = (key: string) => removed.some((orderId) => key.startsWith(`${orderId}:`));
    const forget = <T,>(record: Record<string, T>) => {
      const entries = Object.entries(record);
      return entries.some(([key]) => ofRemoved(key)) ? Object.fromEntries(entries.filter(([key]) => !ofRemoved(key))) : record;
    };
    const forgetKeys = (keys: Set<string>) => (Array.from(keys).some(ofRemoved) ? new Set(Array.from(keys).filter((key) => !ofRemoved(key))) : keys);
    setDropQuantities(forget);
    autoCargoQuantitiesRef.current = forget(autoCargoQuantitiesRef.current);
    setCargoPairChoices(forget);
    setQuantityTouchedKeys(forgetKeys);
    setExcludedCargoKeys(forgetKeys);
    const currentItems = (form.getFieldValue('cargoItemsByKey') || {}) as Record<string, CargoItemFormValue>;
    const nextItems = forget(currentItems);
    if (nextItems !== currentItems) form.setFieldValue('cargoItemsByKey', nextItems);
    // Runs on the selection change only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrderIds]);

  const markQuantityTouched = useCallback((key: string) => {
    setCargoEdited(true);
    setQuantityTouchedKeys((previous) => {
      if (previous.has(key)) return previous;
      const next = new Set(previous);
      next.add(key);
      return next;
    });
  }, []);

  const updateStickySummaryHeight = useCallback((height: number) => {
    setStickySummaryHeight((currentHeight) =>
      currentHeight === height ? currentHeight : height
    );
  }, []);

  // "Przeliczniki ładunku": m² / m³ of the loaded lines, omitted when zero
  // (LDM was removed with pallets, decision 2026-09-30).
  const formatMetricsSummary = (metrics: ReturnType<typeof calculateCargoItemsMetrics>): string => [
    metrics.areaSqm ? `${formatLocalizedNumber(metrics.areaSqm, 3, i18n.language)} m²` : null,
    metrics.volume ? `${formatLocalizedNumber(metrics.volume, 3, i18n.language)} m³` : null,
  ]
    .filter(Boolean)
    .join(' / ') || '—';

  // Cargo totals are derived values: auto-recalculated whenever the loaded
  // quantities or the selected order set change, and never edited by hand.
  // The row key + metrics-input snapshot (buildCargoRowsMetricsKey) keeps this
  // effect from re-running on every render while still catching every input
  // the m²/m³ summary depends on.
  const cargoRowsKey = useMemo(() => buildCargoRowsMetricsKey(cargoRows), [cargoRows]);
  const selectedOrdersKey = useMemo(() => selectedOrderIds.join(','), [selectedOrderIds]);

  useEffect(() => {
    const metrics = calculateCargoItemsMetrics(selectedCargoItems);
    form.setFieldsValue({
      totalWeight: metrics.weight,
      totalVolume: metrics.volume,
      dimensions: selectedCargoItems.length > 0 ? formatMetricsSummary(metrics) : '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cargoRowsKey, selectedOrdersKey]);

  const hasMissingDimensions = hasMissingCargoDimensions(selectedCargoItems);

  const handleFinish = async (values: SharedTransportFormValues) => {
    // Etap 2: the invariant is "at least one link, customer OR supplier order"
    // — an inbound-only truck ("zamówili sobie na magazyn") is valid, and so is
    // unlinking every supplier order as long as a customer order remains. This
    // mirrors the backend's TRANSPORT_HAS_NO_ORDERS guard, so the only case
    // stopped here is the one the backend would refuse anyway.
    // Decision 2026-10-04: the kind says which orders the truck needs.
    if (!kind) {
      feedback.warning(t('transport:kind.required'), { sticky: true });
      focusRequiredField({ id: 'kind', kind: 'kind' });
      return;
    }
    // A saved factory truck that had no ZD already (WP14 allows it with a
    // confirmed warning) keeps saving; only new trucks / a removed ZD stop here.
    if (kindCarriesSupplierOrders(kind) && selectedSupplierOrderIds.length === 0 && !savedWithoutSupplierOrders) {
      feedback.warning(t('transport:kind.supplierOrderRequired'), { sticky: true });
      focusRequiredField({ id: 'supplierOrder', kind: 'supplierOrder' });
      return;
    }
    if (selectedOrderIds.length === 0 && selectedSupplierOrderIds.length === 0) {
      feedback.warning(t('transport:supplierOrders.selectAtLeastOneOrderOrSupplierOrder'), { sticky: true });
      focusRequiredField({ id: 'order', kind: 'order' });
      return;
    }
    // The last ZK leaves a saved truck through the unlink (EditTransportRoute);
    // without a saved ZD staying on it that would delete the truck: one message,
    // before any save question (review 2026-10-12). Same lists as the payload.
    if (transport && planSavedCustomerOrderUnlink(
      { orderIds: selectedOrderIds, supplierOrderIds: selectedSupplierOrderIds },
      transport,
    ).blocked) {
      feedback.warning(t('transport:kind.editLockedCustomerOrders'), { sticky: true });
      return;
    }
    // 2026-10-09 (prod trucks with only a typed „Klient”, ZK 03/12/2025 left
    // as „Nadwyżka”): a ZD line with no customer whose goods match an open ZK
    // asks before it saves as goods for the warehouse. A warning, never a block.
    // Edit: only when the load changed — a rate or date edit does not ask again.
    // Follow-up v3.16: a question answered „save anyway” is not asked again
    // on the next „Zapisz” (after a failed save) while it is about the same.
    const loadChanged = !isEditMode || cargoEdited || supplierCargoTouched;
    const matchingOrders = loadChanged ? unassignedMatches(goodsRecipients) : [];
    const warehouseOnlyKey = `warehouse:${matchingOrders.map((order) => order.orderId).sort().join(',')}`;
    if (matchingOrders.length > 0 && !confirmedSaveQuestionsRef.current.has(warehouseOnlyKey)) {
      if (!(await confirmWarehouseOnly(matchingOrders))) return;
      confirmedSaveQuestionsRef.current.add(warehouseOnlyKey);
    }
    // Owner 2026-10-10: „Fabryka → klient” with no ZK would reload as
    // „Fabryka → magazyn” without a word — say so before saving.
    const noCustomerKey = `noCustomer:${[...selectedSupplierOrderIds].sort().join(',')}`;
    if (matchingOrders.length === 0 && kind === 'FACTORY_TO_CUSTOMER' && selectedOrderIds.length === 0
      && !confirmedSaveQuestionsRef.current.has(noCustomerKey)) {
      if (!(await confirmCustomerKindWithoutOrders())) return;
      confirmedSaveQuestionsRef.current.add(noCustomerKey);
    }

    // Plan 2026-10-13: a load heavier than the trailer (22 800 kg) asks once
    // before saving — e.g. a whole ZK on one truck. Both sides weigh the same
    // goods; the heavier one counts. A delivered truck's plan is history.
    const loadKg = Math.max(
      calculateCargoItemsMetrics(selectedCargoItems).weight || 0,
      isInbound ? supplierCargoWeight(supplierCargoLines, effectiveSupplierCargoEntries).kg : 0,
    );
    const payloadKey = `payload:${Math.round(loadKg)}`;
    if (loadChanged && status !== 'DELIVERED' && loadKg > TRAILER_PAYLOAD_KG
      && !confirmedSaveQuestionsRef.current.has(payloadKey)) {
      if (!(await confirmOverPayload(loadKg))) return;
      confirmedSaveQuestionsRef.current.add(payloadKey);
    }

    if (allocationHintsLoading) {
      feedback.warning(t('transport:cargo.availabilityLoading'), { sticky: true });
      return;
    }

    if (allocationHintsError) {
      feedback.error(t('transport:cargo.availabilityLoadError'), { sticky: true });
      return;
    }

    if (hasMissingCustomerCargo) {
      feedback.error(t('transport:cargo.orderWithoutCargoBlocked', {
        numbers: missingCargoOrderNumbers.join(', '),
      }), { sticky: true });
      focusRequiredField({ id: 'order', kind: 'order' });
      return;
    }

    // Addendum A: a typed ZD quantity must be positive (an empty field means
    // "the whole rest" and is fine).
    const invalidSupplierCargo = supplierCargoLines.some((line) => {
      const entry = supplierCargoEntries[line.itemId];
      return entry && entry.quantity !== null && !(entry.quantity > 0);
    });
    if (isInbound && invalidSupplierCargo) {
      feedback.warning(t('transport:supplierCargo.quantityPositive'), { sticky: true });
      return;
    }

    try {
      await form.validateFields([['cargoItemsByKey']], { recursive: true });
    } catch (validationError) {
      // A rejected cargo rule used to end the submit silently (B1).
      feedback.warning(t('transport:messages.fillRequired'), { sticky: true });
      const firstError = (validationError as { errorFields?: Array<{ name: Array<string | number> }> })?.errorFields?.[0];
      if (firstError) {
        sections?.openAll();
        window.setTimeout(() => form.scrollToField(firstError.name, { behavior: 'smooth', block: 'center', focus: true }), sections ? 250 : 0);
      }
      return;
    }

    const payload = buildSharedTransportPayload(
      values,
      selectedOrderIds,
      selectedCargoItems,
      selectedSupplierOrderIds,
      // Touched guard: only an INBOUND truck whose ZD quantities the operator
      // changed sends them; otherwise the key is omitted (server keeps rows).
      // A customer-sum default (factory → customer) is sent like a typed one.
      buildSupplierCargoPayload(
        supplierCargoLines,
        effectiveSupplierCargoEntries,
        isInbound && (supplierCargoTouched || Object.keys(derivedSupplierCargo).length > 0),
        selectedSupplierOrderIds,
      ),
      // PR3: the pair of every ZK cargo row, only on a factory → customer truck.
      pairingActive
        ? new Map(selectedCargoItems.map((item) => [
          item.orderProductId || '',
          cargoPairs.get(buildCargoKey(item.orderId, item.orderProductId || ''))?.lineId ?? null,
        ]))
        : undefined,
    );
    if (!canEditTransportCosts) {
      delete payload.transportRate;
      delete payload.currency;
      delete payload.invoiceNumber;
    }

    // Review 2026-10-07: a save that moves freight between the ZD of this truck
    // (their batches, then FIFO into the customer margin) says so first.
    const freightShifts = isEditMode && isInbound && canEditTransportCosts && transport
      ? supplierFreightShifts(
        inboundPreviewRows.map((row) => ({
          id: row.key,
          supplierOrderId: inboundPreviewLines.find((line) => line.id === row.key)?.groupId || '',
          supplierOrderLabel: row.orderLabel,
          newPLN: row.amountPLN,
        })),
        (transport.supplierOrders || []).map((supplierOrder) => ({
          supplierOrderId: supplierOrder.id,
          supplierOrderLabel: supplierOrder.displayOrderNumber || supplierOrder.orderNumber,
          linePLN: (supplierOrder.loadItems || []).map((item) => item.freightPLN ?? null),
        })),
      )
      : [];
    if (freightShifts.length > 0 && !(await confirmFreightShift(freightShifts))) return;

    await onSubmit(payload);
  };

  // Save questions answered „save anyway”, one key each (see handleFinish):
  // answering one never forgets another (review 2026-10-13).
  const confirmedSaveQuestionsRef = useRef<Set<string>>(new Set());
  const confirmCustomerKindWithoutOrders = () => new Promise<boolean>((resolve) => {
    modal.confirm({
      title: t('transport:recipients.noCustomerTitle'),
      width: 560,
      content: <p data-testid="transport-no-customer-guard">{t('transport:recipients.noCustomerBody')}</p>,
      okText: t('transport:recipients.noCustomerSave'),
      cancelText: t('transport:recipients.guardAssign'),
      onOk: () => resolve(true),
      onCancel: () => {
        resolve(false);
        sections?.openAll();
        window.setTimeout(() => document.querySelector('[data-testid="transport-goods-recipients"], [data-testid="transport-leg-unloading"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), sections ? 250 : 0);
      },
    });
  });

  const confirmOverPayload = (loadKg: number) => new Promise<boolean>((resolve) => {
    const kg = (value: number) => formatLocalizedNumber(Math.round(value), 0, i18n.language);
    modal.confirm({
      title: t('transport:payloadGuard.title'),
      width: 560,
      content: (
        <p data-testid="transport-payload-guard">
          {t('transport:payloadGuard.body', { weight: kg(loadKg), limit: kg(TRAILER_PAYLOAD_KG), over: kg(loadKg - TRAILER_PAYLOAD_KG) })}
        </p>
      ),
      okText: t('transport:payloadGuard.save'),
      cancelText: t('transport:payloadGuard.back'),
      onOk: () => resolve(true),
      onCancel: () => {
        resolve(false);
        sections?.openAll();
        window.setTimeout(() => document.querySelector(`#${TRANSPORT_ORDER_SELECTION_ANCHOR_ID}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), sections ? 250 : 0);
      },
    });
  });

  const confirmWarehouseOnly = (orders: RecipientCandidate[]) => new Promise<boolean>((resolve) => {
    modal.confirm({
      title: t('transport:recipients.guardTitle'),
      width: 560,
      content: (
        <p data-testid="transport-recipients-guard">
          {t('transport:recipients.guardBody', {
            orders: orders.map((order) => `${order.orderLabel} (${order.customerName})`).join(', '),
          })}
        </p>
      ),
      okText: t('transport:recipients.guardSaveWarehouse'),
      cancelText: t('transport:recipients.guardAssign'),
      onOk: () => resolve(true),
      onCancel: () => {
        resolve(false);
        sections?.openAll();
        // Owner 2026-10-12: „Przypisz klientów” turns the truck into „Z fabryki do klientów”.
        if (kind !== 'FACTORY_TO_CUSTOMER') applyKind('FACTORY_TO_CUSTOMER');
        window.setTimeout(() => document.querySelector('[data-testid="transport-drop-board"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), sections ? 250 : 0);
      },
    });
  });

  // Functional updates only (review 2026-10-12): an async drop must not
  // restore a selection that changed while it waited.
  // (The availability effect raises its own loading flag on the change.)
  const selectOrderIds = (update: (previous: string[]) => string[]) => {
    setCargoEdited(true);
    setSelectedOrderIds(update);
  };
  const handleAssignRecipient = (lineId: string, orderId: string, orderProductId: string) => {
    selectOrderIds((previous) => (previous.includes(orderId) ? previous : [...previous, orderId]));
    setCargoPairChoices((previous) => ({ ...previous, [buildCargoKey(orderId, orderProductId)]: lineId }));
  };
  const handleUnassignRecipient = (orderId: string, orderProductId: string) => {
    setCargoPairChoices((previous) => ({ ...previous, [buildCargoKey(orderId, orderProductId)]: null }));
    selectOrderIds((previous) => (previous.includes(orderId) ? previous.filter((id) => id !== orderId) : previous));
  };

  const confirmFreightShift = (shifts: SupplierFreightShift[]) => new Promise<boolean>((resolve) => {
    // The split moves only through the key or what each ZD carries (a new rate scales all alike);
    // name only what really changed (review F5).
    const keyChanged = costAllocationMethod !== (transport?.costAllocationMethod || DEFAULT_ALLOCATION_METHOD);
    const savedZdIds = (transport?.supplierOrders || []).map((supplierOrder) => supplierOrder.id).sort().join('|');
    const zdChanged = [...selectedSupplierOrderIds].sort().join('|') !== savedZdIds;
    const pairChanged = Object.keys(cargoPairChoices).length > 0;
    const unpairedCounted = unpairedGuess.linesByRow.size > 0;
    const reasons = [
      keyChanged ? t('transport:freightShift.reasonKey') : null,
      zdChanged ? t('transport:freightShift.reasonZd') : null,
      supplierCargoTouched ? t('transport:freightShift.reasonTyped') : null,
      pairChanged ? t('transport:freightShift.reasonPair') : null,
      cargoEdited && !pairChanged ? t('transport:freightShift.reasonCargo') : null,
    ].filter(Boolean);
    if (reasons.length === 0) reasons.push(t('transport:freightShift.reasonDefaults'));
    if (unpairedCounted) reasons.push(t('transport:freightShift.reasonUnpaired'));
    modal.confirm({
      title: t('transport:freightShift.title'),
      width: 560,
      content: (
        <div data-testid="transport-freight-shift-confirm">
          <ul>
            {shifts.map((shift) => (
              <li key={shift.supplierOrderId}>
                {t('transport:freightShift.line', {
                  label: shift.label,
                  before: formatMoneyPl(shift.beforePLN, 'PLN'),
                  after: formatMoneyPl(shift.afterPLN, 'PLN'),
                  difference: `${shift.differencePLN > 0 ? '+' : '−'}${formatMoneyPl(Math.abs(shift.differencePLN), 'PLN')}`,
                })}
              </li>
            ))}
          </ul>
          <p>{t('transport:freightShift.why', { reasons: reasons.join('; ') })}</p>
          <p>{t('transport:freightShift.approx')}</p>
          <p>{t('transport:freightShift.where')}</p>
        </div>
      ),
      okText: t('transport:freightShift.ok'),
      cancelText: t('transport:freightShift.back'),
      onOk: () => resolve(true),
      onCancel: () => {
        resolve(false);
        sections?.openAll();
        window.setTimeout(() => document.querySelector('[data-testid="transport-leg-loading"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), sections ? 250 : 0);
      },
    });
  });

  const handleSupplierOrderChange = useCallback((nextSupplierOrderIds: string[]) => {
    setCargoEdited(true);
    setSelectedSupplierOrderIds(Array.from(new Set(nextSupplierOrderIds.filter(Boolean))));
  }, []);

  // Decision 2026-10-04: the kind owns the direction and the order types.
  // Switching to a kind without ZK/ZD drops that selection (edit mode greys
  // out a kind that would drop saved customer orders — see kindDisabledReasons).
  const applyKind = useCallback((nextKind: TransportKind) => {
    setKind(nextKind);
    form.setFieldValue('direction', transportKindDirection(nextKind));
    if (!kindCarriesCustomerOrders(nextKind)) {
      setSelectedOrderIds([]);
      setAllocationHintsLoading(false);
    }
    if (!kindCarriesSupplierOrders(nextKind)) setSelectedSupplierOrderIds([]);
  }, [form]);
  // Review F2: a kind switch that drops chosen orders or moves a saved truck's
  // freight to the other side of the books asks first, naming both.
  const handleKindChange = (nextKind: TransportKind) => {
    if (nextKind === kind) return;
    const droppedOrders = kindCarriesCustomerOrders(nextKind) ? [] : selectedOrderIds.map((orderId) => {
      const order = availableOrders.find((candidate) => candidate.id === orderId)
        || transport?.routeOrders.find((row) => row.orderId === orderId)?.order;
      return order?.displayOrderNumber || order?.orderNumber || orderId;
    });
    const droppedSupplierOrders = kindCarriesSupplierOrders(nextKind) ? [] : selectedSupplierOrderIds.map((supplierOrderId) => {
      const saved = transport?.supplierOrders?.find((row) => row.id === supplierOrderId);
      if (saved) return saved.displayOrderNumber || saved.orderNumber;
      const candidate = availableSupplierOrders.find((row) => row.id === supplierOrderId);
      return candidate ? getSupplierOrderSummary(candidate).displayNumber : supplierOrderId;
    });
    const currentDirection = form.getFieldValue('direction') as TransportDirection | undefined;
    const nextDirection = transportKindDirection(nextKind);
    const freightMoves = isEditMode && Boolean(currentDirection) && currentDirection !== nextDirection;
    if (droppedOrders.length === 0 && droppedSupplierOrders.length === 0 && !freightMoves) {
      applyKind(nextKind);
      return;
    }
    modal.confirm({
      title: t('transport:kind.changeConfirmTitle', { kind: t(`transport:kind.options.${nextKind}.label`) }),
      content: (
        <div data-testid="transport-kind-change-confirm">
          {droppedOrders.length > 0 ? (
            <p>{t('transport:kind.changeDropsOrders', { numbers: droppedOrders.join(', ') })}</p>
          ) : null}
          {droppedSupplierOrders.length > 0 ? (
            <p>{t('transport:kind.changeDropsSupplierOrders', { numbers: droppedSupplierOrders.join(', ') })}</p>
          ) : null}
          {freightMoves ? (
            <p>{t(nextDirection === 'OUTBOUND'
              ? 'transport:kind.changeFreightToCustomerOrders'
              : 'transport:kind.changeFreightToStock')}</p>
          ) : null}
        </div>
      ),
      okText: t('transport:kind.changeConfirmOk'),
      cancelText: t('common:cancel'),
      onOk: () => applyKind(nextKind),
    });
  };
  const kindDisabledReasons: Partial<Record<TransportKind, string>> = onlyWarehousePurchases && kind !== 'FACTORY_TO_CUSTOMER'
    ? { FACTORY_TO_CUSTOMER: t('orders:supplierOrders.card.originWarehouse') }
    : {};

  // „Podział kosztu auta” (decision 2026-10-04): live, display only.
  // Edit mode: the same rule as the server (resolveSavedPreviewRate).
  const savedPreviewRate = useMemo(
    () => resolveSavedPreviewRate(transport, currency, transportRate),
    [currency, transport, transportRate],
  );
  const previewRate = useTransportPreviewRate(currency, canEditTransportCosts && Boolean(kind), savedPreviewRate);
  const supplierCargoStates = resolveSupplierCargoLines(supplierCargoLines, effectiveSupplierCargoEntries);
  const savedLineFreight = new Map((transport?.supplierOrders || [])
    .flatMap((supplierOrder) => (supplierOrder.loadItems || []).map((item) => [item.id, item.freightPLN ?? null] as const)));
  const inboundPreviewLines: InboundPreviewLine[] = isInbound ? supplierCargoLines.flatMap((line) => {
    const set = supplierCargoFreightSet(line, supplierCargoStates.get(line.itemId), status);
    const unit = line.unit || 'pcs';
    const quantity = set ? getQuantitySetValue(set, unit) : null;
    if (!set || !(Number(quantity) > 0)) return [];
    const ordered = getQuantitySetValue(line.ordered, unit);
    const item = availableSupplierOrders
      .find((supplierOrder) => supplierOrder.id === line.supplierOrderId)?.items
      ?.find((candidate) => candidate.id === line.itemId);
    const valuePLN = item?.totalCostPLN != null && ordered
      ? roundMoney(Number(item.totalCostPLN) * (Number(quantity) / ordered))
      : null;
    return [{
      id: line.itemId,
      groupId: line.supplierOrderId,
      orderLabel: line.supplierOrderLabel,
      name: line.name,
      dimensions: line.dimensions,
      quantity: Number(quantity),
      unit,
      pcs: set.pcs,
      m2: set.m2,
      m3: set.m3,
      width: line.width,
      height: line.height,
      length: line.length,
      thickness: line.thickness,
      countInPiecesOnly: line.countInPiecesOnly,
      valuePLN,
      savedPLN: savedLineFreight.get(line.itemId) ?? null,
    }];
  }) : [];
  const inboundPreviewRows = buildInboundCostSplitPreview({
    lines: inboundPreviewLines,
    method: costAllocationMethod,
    // Empty price = unknown („—”), a typed 0 = 0,00 (follow-up v3.16); same as previewCost below.
    transportCost: canEditTransportCosts && transportRate != null ? Number(transportRate) : null,
    rateToPLN: previewRate.rate?.rate ?? null,
  });
  const savedOutboundPLN: Record<string, number | null> = Object.fromEntries(
    (transport?.routeOrders || []).map((routeOrder) => [routeOrder.orderId, routeOrder.allocatedTransportCost ?? null]),
  );
  const manualDifference = !isInbound && costAllocationMethod === 'MANUAL'
    ? manualSplitDifference(loadedCargoRows.map((row) => row.manualAllocatedTransportCost), transportCostForAllocation)
    : null;
  // Usability round 2026-10-05: name the measure the default key really uses
  // and warn once, listing only the positions that really get 0.
  const defaultKeyLoads = isInbound
    ? Array.from(new Set(inboundPreviewLines.map((line) => line.groupId)))
      .map((groupId) => inboundPreviewLines.filter((line) => line.groupId === groupId))
    : Array.from(new Set(loadedCargoRows.map((row) => row.orderId))).map((orderId) => loadedCargoRows
      .filter((row) => row.orderId === orderId)
      .map((row) => ({
        name: row.productName,
        quantity: Number(row.quantity) || 0,
        unit: row.unit || 'pcs',
        m2: row.quantityM2,
        m3: row.quantityM3,
        width: row.width,
        height: row.height,
        length: row.length,
        thickness: row.thickness,
        countInPiecesOnly: row.countInPiecesOnly === true,
      })));
  const defaultKeyLabel = defaultKeyLabelFor(defaultKeyLoads, isInbound, t);
  const previewCost = canEditTransportCosts && transportRate != null ? Number(transportRate) : null;
  // Shares decide (review PR #269): also before a cost is typed, and for the
  // ZK value key once the server's shares are known (null = not yet known).
  const zeroNames = costAllocationMethod === 'MANUAL'
    ? []
    : isInbound
      ? (inboundPreviewRows.length > 1
        ? inboundPreviewRows.filter((row) => !(Number(row.sharePercent) > 0)).map((row) => `${row.orderLabel} — ${row.name}`)
        : [])
      : (allocationPreview.length > 1
        ? allocationPreview
          .filter((row) => row.allocationPercent !== null && !(Number(row.allocationPercent) > 0))
          .map((row) => row.displayOrderNumber)
        : []);

  // Round 2 (U9): manual amounts per ZK in the split table; each order's
  // amount is spread over its lines by their current manual amounts (else
  // their quantities) — the server's line split reads the same amounts.
  const manualInSplitTable = !isInbound && costAllocationMethod === 'MANUAL' && canEditTransportCosts;
  const manualByOrderId: Record<string, number | null> = {};
  // An order with no typed amount stays empty (not „0,00”).
  loadedCargoRows.forEach((row) => {
    const raw = row.manualAllocatedTransportCost;
    const amount = raw === null || raw === undefined || (raw as unknown) === '' ? NaN : Number(raw);
    if (!Number.isFinite(amount)) return;
    manualByOrderId[row.orderId] = Math.round(((manualByOrderId[row.orderId] ?? 0) + amount) * 100) / 100;
  });
  const setManualOrderAmount = (orderId: string, amount: number | null) => {
    const rows = loadedCargoRows.filter((row) => row.orderId === orderId);
    if (rows.length === 0) return;
    const current = (form.getFieldValue('cargoItemsByKey') || {}) as Record<string, CargoItemFormValue>;
    const manualWeights = rows.map((row) => Math.max(Number(current[row.key]?.manualAllocatedTransportCost) || 0, 0));
    const weights = manualWeights.some((value) => value > 0) ? manualWeights : rows.map((row) => Math.max(Number(row.quantity) || 0, 0));
    const amounts = amount === null ? rows.map(() => null) : allocateLargestRemainder(amount, weights);
    const next = { ...current };
    rows.forEach((row, index) => { next[row.key] = { ...next[row.key], manualAllocatedTransportCost: amounts[index] }; });
    form.setFieldsValue({ cargoItemsByKey: next });
  };

  // PR3: „z pozycji ZD” of one ZK cargo row — ZD lines of the same product
  // on this truck, labelled „ZD/… · nazwa · ilość na aucie”.
  // The position tells identical lines apart; the
  // amount is what THIS truck carries, or the ordered amount when it carries 0.
  const pairingLineLabel = (line: (typeof supplierCargoLines)[number]): string => {
    const carried = supplierCargoStates.get(line.itemId)?.carried;
    const carriedAmount = carried && line.unit ? getQuantitySetValue(carried, line.unit) : null;
    const orderedAmount = line.unit ? getQuantitySetValue(line.ordered, line.unit) : null;
    const unitLabel = formatUnit(line.unit, i18n.language);
    const amountLabel = carriedAmount !== null && carriedAmount > 0
      ? `${formatLocalizedQuantity(carriedAmount, i18n.language)} ${unitLabel}`
      : orderedAmount !== null
        ? t('transport:pairing.orderedOnZd', { quantity: `${formatLocalizedQuantity(orderedAmount, i18n.language)} ${unitLabel}` })
        : null;
    return [
      line.supplierOrderLabel,
      line.position ? t('transport:linePosition', { number: line.position }) : null,
      line.name,
      line.dimensions,
      amountLabel,
    ].filter(Boolean).join(' · ');
  };
  const renderCargoPairing = pairingActive ? (record: (typeof cargoRows)[number]) => {
    const pair = cargoPairs.get(record.key);
    const options = [...(pair?.candidates || [])];
    const current = pair?.lineId ? supplierCargoLines.find((line) => line.itemId === pair.lineId) : undefined;
    if (current && !options.includes(current)) options.push(current);
    return (
      <TransportCargoPairingField
        rowKey={record.key}
        productName={record.productName}
        value={pair?.lineId ?? null}
        carriesGoods={Number(record.quantity || 0) > 0}
        options={options.map((line) => ({ value: line.itemId, label: pairingLineLabel(line) }))}
        guessedLabel={(() => {
          const guessed = (unpairedGuess.linesByRow.get(record.key) || [])
            .map((lineId) => supplierCargoLines.find((line) => line.itemId === lineId))
            .filter((line): line is NonNullable<typeof line> => Boolean(line));
          return guessed.length > 0 ? guessed.map((line) => [line.supplierOrderLabel, line.name, line.dimensions].filter(Boolean).join(' · ')).join(' + ') : null;
        })()}
        onChange={(value) => {
          setCargoEdited(true);
          setCargoPairChoices((previous) => ({ ...previous, [record.key]: value }));
        }}
      />
    );
  } : undefined;

  const costSplitPreview = kind ? (
    <TransportCostSplitPreview
      direction={isInbound ? 'INBOUND' : 'OUTBOUND'}
      currency={currency || DEFAULT_TRANSPORT_CURRENCY}
      transportCost={previewCost}
      inboundRows={inboundPreviewRows}
      outboundRows={allocationPreview}
      savedOutboundPLN={savedOutboundPLN}
      valueComputedOnSave={!isInbound && valueKey}
      manualDifference={manualDifference}
      manualPrefilled={manualPrefilled}
      zeroNames={zeroNames}
      manualByOrderId={manualInSplitTable ? manualByOrderId : undefined}
      onManualOrderAmount={manualInSplitTable ? setManualOrderAmount : undefined}
      rate={previewRate.rate}
      rateLoading={previewRate.loading}
      compareWithSaved={(currency || DEFAULT_TRANSPORT_CURRENCY) === 'PLN' || Boolean(savedPreviewRate)}
    />
  ) : null;

  // Usability round 2026-10-05: the order picker marks a ZK that already
  // rides another truck with that truck's number (advisory lookup).
  const [orderTruckMarks, setOrderTruckMarks] = useState<CustomerOrderTruckMarks>({ coverage: {}, trucks: {} });
  const openOrderIdsKey = availableOrders
    .filter((order) => !isClosedOrderStatus(order.status))
    .map((order) => order.id)
    .slice(0, 2000)
    .join(',');
  useEffect(() => {
    let active = true;
    const ids = openOrderIdsKey ? openOrderIdsKey.split(',') : [];
    if (ids.length === 0) return undefined;
    void fetchCustomerOrderTruckMarks(ids).then((marks) => { if (active) setOrderTruckMarks(marks); });
    return () => { active = false; };
  }, [openOrderIdsKey]);
  const renderOrderTruckMarks = (record: OrderOptionDto): React.ReactNode => {
    const trucks = (orderTruckMarks.trucks[record.id] || []).filter((truck) => truck.id !== transport?.id);
    if (trucks.length === 0) return <Typography.Text type="secondary">—</Typography.Text>;
    return (
      <span className="transport-order-picker-trucks" data-testid="transport-order-picker-trucks">
        {trucks.map((truck) => <Tag key={truck.id}>{truck.label}</Tag>)}
        {orderTruckMarks.coverage[record.id] === 'FULL' ? (
          <Typography.Text type="secondary">{t('transport:orderPicker.wholeOnTrucks')}</Typography.Text>
        ) : null}
      </span>
    );
  };

  const availableColumns: ColumnsType<OrderOptionDto> = [
    {
      title: t('transport:orderNumber'),
      key: 'displayOrderNumber',
      onCell: () => ({
        className: 'transport-order-picker-cell',
        'data-label': t('transport:orderNumber'),
      }),
      render: (_, record) => record.displayOrderNumber || record.orderNumber,
    },
    {
      title: t('transport:customer'),
      dataIndex: 'customerName',
      key: 'customerName',
      onCell: () => ({
        className: 'transport-order-picker-cell',
        'data-label': t('transport:customer'),
      }),
    },
    {
      title: t('transport:table.products'),
      key: 'products',
      onCell: () => ({
        className: 'transport-order-picker-cell',
        'data-label': t('transport:table.products'),
      }),
      render: (_, record) => record.products
        .map(getTransportOrderProductDisplayName)
        .join(', ') || '-',
    },
    {
      title: t('transport:statusColumn'),
      dataIndex: 'status',
      key: 'status',
      onCell: () => ({
        className: 'transport-order-picker-cell',
        'data-label': t('transport:statusColumn'),
      }),
      // The `availableOrders` rows carry ORDER statuses (NEW_INQUIRY,
      // QUOTE_SENT, ...), not TRANSPORT statuses (PENDING, SCHEDULED, ...).
      // Look up the translation in the orders namespace; fall back to a
      // a neutral operator-facing label if a new status lands before its
      // translation. Never expose the backend enum in Polish UI.
      render: (value) => (
        <Tag>
          {t(`orders:statusOptions.${value}`, {
            defaultValue: t('common:unknownStatus'),
          })}
        </Tag>
      ),
    },
    {
      title: t('transport:orderPicker.onTrucks'),
      key: 'onTrucks',
      onCell: () => ({
        className: 'transport-order-picker-cell',
        'data-label': t('transport:orderPicker.onTrucks'),
      }),
      render: (_, record) => renderOrderTruckMarks(record),
    },
  ];

  const previewColumns: ColumnsType<SharedTransportPreviewRow> = [
    {
      title: t('transport:orderNumber'),
      dataIndex: 'displayOrderNumber',
      key: 'displayOrderNumber',
    },
    {
      title: t('transport:customer'),
      dataIndex: 'customerName',
      key: 'customerName',
    },
    {
      title: t('transport:cargoQuantity'),
      key: 'cargoQuantity',
      render: (_, record) => formatCargoQuantityTotals(record.metrics.quantityByUnit, i18n.language, ' · '),
    },
    {
      title: t('transport:weight'),
      key: 'weight',
      render: (_, record) => `${formatLocalizedNumber(record.metrics.weight, 2, i18n.language)} kg`,
    },
    {
      title: t('transport:allocation.basis'),
      key: 'basisValue',
      // Goods value: never computed here and hidden from OPERATIONS — "—".
      render: (_, record) => (record.basisValue === null
        ? '—'
        : formatLocalizedNumber(record.basisValue, 3, i18n.language)),
    },
    {
      title: t('transport:allocation.percent'),
      key: 'allocationPercent',
      render: (_, record) => (record.allocationPercent === null
        ? t('transport:allocationKey.computedOnSave')
        : formatLocalizedPercent(record.allocationPercent, i18n.language, 2)),
    },
    ...(canEditTransportCosts ? [{
      title: t('transport:allocation.cost'),
      key: 'allocatedTransportCost',
      // Saved value-key shares are PLN-canonical; the live split uses the form currency.
      render: (_: unknown, record: SharedTransportPreviewRow) => (record.allocatedTransportCost === null
        ? '—'
        : formatMoneyPl(Number(record.allocatedTransportCost || 0), valueKey ? 'PLN' : (currency || DEFAULT_TRANSPORT_CURRENCY))),
    }] : []),
  ];

  // Polish-first money text (S36 follow-up, live-behavior observation
  // 2026-07-07): toFixed(2) rendered en-US decimals ("3400.00 PLN") in the
  // create-form footer/preview — route through the formatMoneyPl single home
  // instead; '\u2014' em dash for empty, unified with the detail page.
  const formatCurrencyAmount = (value: number | null | undefined): string =>
    value == null ? '\u2014' : formatMoneyPl(Number(value), currency || DEFAULT_TRANSPORT_CURRENCY);

  const renderAllocationWarning = (
    warning: TransportCostAllocationWarning,
    index: number,
  ) => {
    const difference = formatCurrencyAmount(transportCostAllocation.summary.allocationDifference);
    // Offending product names for MISSING_DIMENSIONS, so the operator knows which
    // cargo lines lack dimensions. Unnamed lines are dropped; if none remain we
    // fall back to the generic message with no name suffix.
    const missingDimensionNames =
      ['MISSING_DIMENSIONS', 'MISSING_VALUE', 'COUNTED_IN_PIECES_ONLY'].includes(warning.code)
        ? (warning.items || [])
            .map((item) => (item.name || '').trim())
            .filter((name) => name.length > 0)
            .join(', ')
        : '';

    let description: React.ReactNode;
    if (warning.code === 'MANUAL_SUM_MISMATCH') {
      description = t('transport:commercialAllocationWarningDifference', { difference });
    } else if (missingDimensionNames) {
      description = t('transport:missingDimensionsItems', { names: missingDimensionNames });
    }

    return (
      <Alert
        key={`${warning.code}-${warning.itemId || index}`}
        type="warning"
        showIcon
        message={t(`transport:${commercialAllocationWarningKey(warning.code)}`)}
        description={description}
        style={{ marginTop: 8 }}
      />
    );
  };

  const orderRowSelection = {
    selectedRowKeys: selectedOrderIds,
    onChange: (keys: React.Key[]) => {
      const nextIds = keys.map((key) => String(key));
      setCargoEdited(true);
      setAllocationHintsLoading(nextIds.length > 0);
      setAllocationHintsError(false);
      setSelectedOrderIds(nextIds);
    },
  };

  // Duplicate-submit-button fix (coordinator triage, 2026-07-07): this form
  // used to render the SAME submit/cancel pair in TWO places — a top action
  // bar and TransportStickySummary's footer bar — parametrized by `location`
  // purely so their duplicate accessible names didn't collide in
  // `getByRole('button', { name: ... })`-style test lookups. The top copy is
  // now gone (the fixed TransportStickySummary is the ONLY submit surface and
  // remains visible throughout the scroll range). No more
  // location parameter needed since there is exactly one instance again, but
  // the `-sticky` suffix on the testid/aria-label is kept (rather than
  // reverted to a bare id) so existing `-sticky` references elsewhere do not
  // need to change; only `-top` lookups needed updating (see the `-top`
  // search below — the bare id was never in use).
  // Owner 2026-10-10, variant B: drag goods onto a customer stop.
  const rawBoard = kind === 'FACTORY_TO_CUSTOMER' && goodsRecipients.length > 0
    ? buildDropBoard(
      directDeliveryLines,
      new Map(supplierCargoLines.map((line) => {
        const carried = supplierCargoStates.get(line.itemId)?.carried;
        return [line.itemId, carried ? setValue(carried, line.unit) : null];
      })),
      availableOrders,
      selectedOrderIds,
      cargoRows,
      new Map(Array.from(cargoPairs.entries()).map(([key, pair]) => [key, pair.lineId])),
      extraStopIds,
    )
    : null;
  // Owner 2026-10-14: editing a truck adds no new ZK — a ZK joins from its own
  // card. „Gdzie jedzie” keeps the ZK saved on this truck (moving goods, order,
  // taking one off and back), without suggestions or „Dodaj innego klienta”.
  // Valentyna 2026-11-02 (TR-ZK-2/09/2026-1, delivered, saved „na magazyn”):
  // the ZK a ZD on this truck was generated from is that ZD's own customer,
  // not a new ZK — it stays offered, or such a truck can never reach it.
  const keptOrderIds = new Set([
    ...(transport?.routeOrders || []).map((routeOrder) => routeOrder.orderId),
    ...directDeliveryLines.map((line) => line.sourceOrderId).filter((id): id is string => Boolean(id)),
  ]);
  const board = rawBoard && isEditMode
    ? {
      ...rawBoard,
      stops: rawBoard.stops.filter((stop) => stop.onTruck || keptOrderIds.has(stop.orderId)),
      addable: rawBoard.addable.filter((stop) => keptOrderIds.has(stop.orderId)),
    }
    : rawBoard;
  const kindRef = useRef(kind);
  kindRef.current = kind;
  const finishDrop = () => {
    dropBusyRef.current = false;
    setDropBusy(false);
    setPendingDrop(null);
  };
  const writeDropQuantities = (quantities: Record<string, number>) => {
    if (Object.keys(quantities).length === 0) return;
    const currentItems = (form.getFieldValue('cargoItemsByKey') || {}) as Record<string, CargoItemFormValue>;
    form.setFieldsValue({
      cargoItemsByKey: {
        ...currentItems,
        ...Object.fromEntries(Object.entries(quantities).map(([key, quantity]) => [key, { ...currentItems[key], quantity }])),
      },
    });
    Object.keys(quantities).forEach(markQuantityTouched);
    setDropQuantities((previous) => ({ ...previous, ...quantities }));
  };
  // A drop on a customer not on the truck: once the ZK is on the truck and its
  // rows exist (create and edit alike), write its quantities from the CURRENT
  // board (planDrop, FA.3), then free the board.
  useEffect(() => {
    if (!pendingDrop) return;
    if (kind !== 'FACTORY_TO_CUSTOMER' || !selectedOrderIds.includes(pendingDrop.orderId)) {
      finishDrop();
      return;
    }
    const order = availableOrders.find((candidate) => candidate.id === pendingDrop.orderId);
    const stop = board?.stops.find((candidate) => candidate.orderId === pendingDrop.orderId && candidate.onTruck);
    if (!order || !board || !stop) {
      finishDrop();
      return;
    }
    if (order.products.some((product) => cargoItemsByKey[buildCargoKey(order.id, product.id)] === undefined)) return;
    writeDropQuantities(planDrop({
      lineId: pendingDrop.lineId,
      goods: board.goods,
      stop,
      targetKey: pendingDrop.targetKey,
      need: pendingDrop.need,
      fresh: true,
      orderProducts: order.products,
      derivedLineIds: customerSumLineIds,
      // Without any customer sum: typed / saved quantities, the rest, or „Nic”.
      baseCarried: new Map(Array.from(resolveSupplierCargoLines(supplierCargoLines, supplierCargoEntries).entries())
        .map(([itemId, state]) => {
          const unit = supplierCargoLines.find((line) => line.itemId === itemId)?.unit ?? null;
          return [itemId, state.carried ? setValue(state.carried, unit) : null];
        })),
    }));
    finishDrop();
    // Runs while a drop waits and the rows it needs change; the board is read as rendered.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDrop, cargoItemsByKey, selectedOrderIds, kind]);

  // Owner 2026-10-12 (film 03/12/2025: one ZK line per truck): a ZK not on this
  // truck yet takes the goods on its first line of the same product that other
  // trucks do not carry already.
  // `auto`: the source-ZK placement below puts nothing on the truck when the ZK
  // has nothing left to take (review PR #415, F5: a second truck for the rest).
  const dropOnNewStop = (lineId: string, stop: BoardStop, droppedOn: BoardStopLine, auto = false) => {
    dropBusyRef.current = true;
    setDropBusy(true);
    void (async () => {
      let remaining: Record<string, number> | null = null;
      try {
        const hints = computeAllocationHints([await fetchOrderTransports(stop.orderId)], transport);
        remaining = Object.fromEntries(stop.lines.map((line) => {
          const hint = hints[line.orderProductId];
          return [line.orderProductId, hint ? Math.max(Number(hint.remainingQuantity || 0), 0) : (line.need ?? 0)];
        }));
      } catch {
        // Coverage unknown: keep the board's pick; the availability effect still caps it.
      }
      if (!mountedRef.current) return;
      // The kind changed while waiting: nothing goes on a truck „na magazyn”.
      if (kindRef.current !== 'FACTORY_TO_CUSTOMER') {
        finishDrop();
        return;
      }
      const { target, need } = pickDropTarget(stop, droppedOn, remaining);
      if (auto && need !== null && need <= 0.0005) {
        finishDrop();
        return;
      }
      setPendingDrop({ lineId, orderId: stop.orderId, targetKey: target.key, need });
      handleAssignRecipient(lineId, stop.orderId, target.orderProductId);
    })();
  };
  // Tomek 2026-10-21: a new truck from a ZD generated from a ZK starts with
  // that ZK as the recipient — as if its goods were dropped on its stop. Once
  // per form: taking the ZK off again keeps it off. It waits for the ZD's
  // other trucks (cargo context): before them a line seems to carry all of
  // the ZD (review PR #415, F6), and it skips a line this truck carries none of.
  useEffect(() => {
    if (isEditMode || sourceRecipientDoneRef.current || !board || dropBusyRef.current) return;
    if (supplierCargoGroups.some((group) => group.loading)) return;
    const source = board.goods
      .filter((item) => item.carried === null || item.carried > 0.0005)
      .map((item) => {
        const stop = board.stops.find((candidate) => candidate.orderId === item.line.sourceOrderId);
        const droppedOn = stop?.lines.find((stopLine) => stopLine.productVariantId === item.line.productVariantId);
        return { item, stop, droppedOn };
      })
      .find((entry) => entry.stop && entry.droppedOn);
    if (!source?.stop || !source.droppedOn) return;
    sourceRecipientDoneRef.current = true;
    if (source.stop.onTruck) return;
    dropOnNewStop(source.item.line.itemId, source.stop, source.droppedOn, true);
    // The board is rebuilt every render; the ref keeps this to one drop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });

  // Owner 2026-10-10: section 2 „Gdzie jedzie” on a factory truck.
  // Owner 2026-10-12: only „Z fabryki do klientów” maps goods to customers.
  const dropBoard = board ? (
              <TransportGoodsDropBoard
                goods={board.goods}
                stops={board.stops}
                reducedByOthers={cargoReducedByOthers}
                addable={board.addable}
                busy={dropBusy}
                warehouseLabel={warehouseAddress || t('transport:legs.warehouse')}
                onDrop={(lineId, stop, droppedOn) => {
                  if (dropBusyRef.current) return;
                  if (!stop.onTruck) {
                    dropOnNewStop(lineId, stop, droppedOn);
                    return;
                  }
                  setCargoEdited(true);
                  setCargoPairChoices((previous) => ({ ...previous, [droppedOn.key]: lineId }));
                  writeDropQuantities(planDrop({
                    lineId, goods: board.goods, stop, targetKey: droppedOn.key, need: droppedOn.need ?? null, fresh: false,
                  }));
                }}
                onQuantity={(stopLine, quantity) => {
                  const currentItems = (form.getFieldValue('cargoItemsByKey') || {}) as Record<string, CargoItemFormValue>;
                  form.setFieldsValue({
                    cargoItemsByKey: { ...currentItems, [stopLine.key]: { ...currentItems[stopLine.key], quantity: quantity ?? 0 } },
                  });
                  markQuantityTouched(stopLine.key);
                }}
                onRemove={(stop, stopLine) => {
                  // Owner 2026-10-12 (edge run A): ✕ takes off this line only; the
                  // ZK leaves the truck when no line of it carries goods any more.
                  const others = stop.lines.filter((line) => line.key !== stopLine.key && line.pairedLineId && (Number(line.onTruck) || 0) > 0);
                  if (others.length === 0) {
                    handleUnassignRecipient(stop.orderId, stopLine.orderProductId);
                    return;
                  }
                  setCargoEdited(true);
                  setCargoPairChoices((previous) => ({ ...previous, [stopLine.key]: null }));
                  const currentItems = (form.getFieldValue('cargoItemsByKey') || {}) as Record<string, CargoItemFormValue>;
                  form.setFieldsValue({
                    cargoItemsByKey: { ...currentItems, [stopLine.key]: { ...currentItems[stopLine.key], quantity: 0 } },
                  });
                  markQuantityTouched(stopLine.key);
                }}
                onRemoveLeftover={({ line, assigned }) => {
                  // Owner 2026-10-10: ✕ on „Magazyn” — the rest nobody takes stays
                  // with the supplier; the truck carries only what customers take.
                  if (!line.unit) return;
                  if (assigned > 0.0005) {
                    handleSupplierCargoChange(line.itemId, { quantity: assigned, unit: line.unit as SupplierCargoUnit });
                    return;
                  }
                  const next = excludeSupplierCargoLine(supplierCargoLines, effectiveSupplierCargoEntries, line.itemId);
                  if (next) {
                    setSupplierCargoTouched(true);
                    setSupplierCargoEntries(next);
                    return;
                  }
                  // The ZD's only line on the truck: the ZD leaves the truck.
                  handleSupplierOrderChange(selectedSupplierOrderIds.filter((id) => id !== line.supplierOrderId));
                }}
                onAddStop={(orderId) => setExtraStopIds((previous) => (previous.includes(orderId) ? previous : [...previous, orderId]))}
                onMoveStop={(orderId, step) => {
                  const index = selectedOrderIds.indexOf(orderId);
                  const target = index + step;
                  if (index < 0 || target < 0 || target >= selectedOrderIds.length) return;
                  const next = [...selectedOrderIds];
                  [next[index], next[target]] = [next[target], next[index]];
                  // The stop order is not the load: no cargoEdited, so an
                  // untyped ZD line keeps „the rest” (follow-up v3.16).
                  setSelectedOrderIds(next);
                }}
              />
  ) : null;

  const stickyGoodsValueRaw = goodsValueKnown ? Number(transportCostAllocation.summary.totalGoodsValue) : NaN;
  const stickyGoodsValue = Number.isFinite(stickyGoodsValueRaw) && stickyGoodsValueRaw > 0 ? stickyGoodsValueRaw : null;
  const stickySubmitButtons = (
    <Space wrap size={[8, 8]}>
      {onCancel ? (
        <Button
          onClick={onCancel}
          icon={<CloseOutlined />}
          data-testid="transport-form-cancel-sticky"
        >
          {t('common:cancel')}
        </Button>
      ) : null}
      <Button
        type="primary"
        htmlType="submit"
        loading={loading}
        // B1 (round 3, 2026-10-05): never disabled — a greyed save that
        // ignored the click after removing a cargo line read as „nie działa”.
        // handleFinish says why it cannot save (cargo, availability, rules).
        icon={<SaveOutlined />}
        data-testid="transport-form-submit-sticky"
        aria-label={`${transport ? t('transport:updateRoute') : t('transport:createTransport')} (sticky)`}
      >
        {transport ? t('transport:updateRoute') : t('transport:createTransport')}
      </Button>
    </Space>
  );

  const cargoStats = calculateCargoItemsMetrics(selectedCargoItems);
  const cargoStatItems: Array<{ key: string; label: string; value: string }> = [
    {
      key: 'cargoQuantity',
      label: t('transport:fields.cargoQuantity'),
      value: selectedCargoItems.length > 0 ? formatCargoQuantityTotals(cargoStats.quantityByUnit, i18n.language, ' · ') : '—',
    },
    {
      key: 'weight',
      label: t('transport:fields.totalWeight'),
      // Missing weight is not 0 kg: unknown when nothing weighs, a lower bound when some lines do.
      value: selectedCargoItems.length === 0 ? '—'
        : cargoStats.weightIncomplete && !(cargoStats.weight > 0) ? t('transport:fields.totalWeightUnknown')
          : cargoStats.weightIncomplete ? t('transport:fields.totalWeightPartial', { weight: formatLocalizedNumber(cargoStats.weight, 2, i18n.language) })
            : `${formatLocalizedNumber(cargoStats.weight, 2, i18n.language)} kg`,
    },
    { key: 'volume', label: t('transport:fields.totalVolume'), value: `${formatLocalizedNumber(cargoStats.volume, 3, i18n.language)} m³` },
    {
      key: 'dimensions',
      label: t('transport:fields.dimensions'),
      value: selectedCargoItems.length > 0 ? formatMetricsSummary(cargoStats) : '—',
    },
  ];
  // F1 (trailer weight indicator): display-only, never blocks save.

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      onFinishFailed={({ errorFields }) => {
        // A refused save must always answer, and stay until read (review 2026-10-06).
        feedback.warning(t('transport:messages.fillRequired'), { sticky: true });
        // The ZK quantities live in a window (owner 2026-10-11): show the field.
        if (errorFields.some((field) => field.name[0] === 'cargoItemsByKey')) setCargoModalOpen(true);
        if (missingRequiredFields[0]) {
          focusRequiredField(missingRequiredFields[0]);
        } else if (errorFields[0]) {
          // A rule-level error may sit in any collapsed panel: reveal them all.
          sections?.openAll();
          window.setTimeout(() => form.scrollToField(errorFields[0].name, {
            behavior: 'smooth',
            block: 'center',
            focus: true,
          }), sections ? 250 : 0);
        }
      }}
      autoComplete="off"
      // HIGH (Enter submit): without this, pressing Enter while e.g. typing
      // "Firma transportowa" bubbles to antd's
      // implicit form submit and CREATES the transport mid-configuration.
      // shouldPreventEnterFormSubmit allows Enter in the notes <textarea> and
      // on the real submit <button>, blocks it everywhere else.
      onKeyDown={(event) => {
        if (shouldPreventEnterFormSubmit(event)) {
          event.preventDefault();
        }
      }}
      // The summary is fixed above the viewport bottom, so the form must reserve
      // enough trailing space for the final fields to scroll clear of it.
      // Reserve the ACTUAL sticky-surface height reported by ResizeObserver,
      // plus its bottom offset and a small focus/scroll clearance. This stays
      // correct when mobile wrapping or a longer translation makes the footer
      // taller; no guessed fixed padding that can regress at another width.
      // the actual last field always has room to scroll fully clear of the
      // fixed strip before running out of page to scroll.
      style={{
        paddingBottom:
          stickySummaryHeight + TRANSPORT_STICKY_OFFSET_BOTTOM + TRANSPORT_STICKY_SCROLL_CLEARANCE,
        // Keep fields that live above the fixed summary scrollable into a
        // clear viewport area. This matters for the inbound ZD picker: a
        // browser click/focus must not land underneath the sticky actions.
        '--transport-form-sticky-clearance': `${
          stickySummaryHeight + TRANSPORT_STICKY_OFFSET_BOTTOM + TRANSPORT_STICKY_SCROLL_CLEARANCE
        }px`,
      } as React.CSSProperties & { '--transport-form-sticky-clearance'?: string }}
    >
      {/* S40 (coordinator triage, 2026-07-07): cargoItemsByKey is registered
          only per leaf field inside the cargo cards, but this component
          watches the PARENT path (`Form.useWatch('cargoItemsByKey')`). On an
          edit-mode load the seed effect writes the persisted snapshot before
          any per-row Form.Item exists, and without a registered Field entity
          for the parent the write is invisible to Form.useWatch. This hidden
          anchor registers the parent path from the very first render. */}
      <Form.Item name="cargoItemsByKey" hidden noStyle>
        <Input />
      </Form.Item>

      {/* Section order (decision 2026-10-04): rodzaj auta → co jedzie →
          trasa i terminy → przewoźnik i koszt. All main sections start open;
          without a kind (new truck, no preset) only the kind choice shows. */}
      <div id={TRANSPORT_KIND_PICKER_ANCHOR_ID}>
        <TransportKindPicker kind={kind} onChange={handleKindChange} disabledReasons={kindDisabledReasons} customerOrderCount={selectedOrderIds.length} />
      </div>
      {kindCarriesSupplierOrders(kind) && selectedSupplierOrderIds.length === 0 && savedWithoutSupplierOrders ? (
        <Alert
          type="warning"
          showIcon
          data-testid="transport-kind-saved-without-supplier-orders"
          message={t('transport:kind.savedWithoutSupplierOrders')}
          style={{ marginTop: 12 }}
        />
      ) : null}

      {kind ? (
      <>
      {/* 1. Co jedzie — picker modal + selected orders + cargo items */}
      <div id={TRANSPORT_ORDER_SELECTION_ANCHOR_ID} tabIndex={-1}>
        <TransportOrdersCargoSection
          transport={transport}
          customerOrdersEnabled={kindCarriesCustomerOrders(kind)}
          kind={kind}
          manualInSplitTable={manualInSplitTable}
          supplierOrdersField={kindCarriesSupplierOrders(kind) ? (
            <TransportSupplierOrdersField
              availableSupplierOrders={kind === 'FACTORY_TO_CUSTOMER'
                ? availableSupplierOrders.filter((order) => generatedSupplierOrderIds.has(order.id))
                : availableSupplierOrders}
              selectedSupplierOrders={transport?.supplierOrders}
              value={selectedSupplierOrderIds}
              onChange={handleSupplierOrderChange}
              loading={supplierOrdersLoading}
              loadError={supplierOrdersLoadError}
              onRetry={onRetrySupplierOrders}
            />
          ) : null}
          supplierOrdersSelectedList={(
            <TransportSelectedSupplierOrdersList
              availableSupplierOrders={availableSupplierOrders}
              selectedSupplierOrders={transport?.supplierOrders}
              value={selectedSupplierOrderIds}
              onChange={handleSupplierOrderChange}
            />
          )}
          // Owner 2026-10-10: „Co jedzie” shows the ZD like the quantities
          // popup (band, colours, trailer) — read-only; editing in the popup.
          supplierLoadView={isInbound ? (
            <TransportSupplierCargoEditor
              readOnly
              groups={supplierCargoGroups}
              entries={effectiveSupplierCargoEntries}
              onChange={handleSupplierCargoChange}
              onRemoveGroup={(supplierOrderId) => handleSupplierOrderChange(selectedSupplierOrderIds.filter((id) => id !== supplierOrderId))}
              derivedItemIds={customerSumLineIds}
            />
          ) : null}
          supplierCargoEditor={isInbound ? (
            <>
              <Button
                icon={<EditOutlined />}
                onClick={() => setSupplierCargoModalOpen(true)}
                data-testid="transport-supplier-cargo-edit-open"
                style={{ justifySelf: 'start', marginTop: 8 }}
              >
                {t('transport:supplierCargo.editButton')}
              </Button>
              <Modal
                open={supplierCargoModalOpen}
                title={t('transport:supplierCargo.editTitle')}
                width={980}
                forceRender
                onCancel={() => setSupplierCargoModalOpen(false)}
                footer={(
                  <Button type="primary" onClick={() => setSupplierCargoModalOpen(false)} data-testid="transport-supplier-cargo-edit-done">
                    {t('transport:supplierCargo.editDone')}
                  </Button>
                )}
              >
                <TransportSupplierCargoEditor
                  groups={supplierCargoGroups}
                  entries={effectiveSupplierCargoEntries}
                  onChange={handleSupplierCargoChange}
                  onExclude={(itemId) => {
                    // „Nic” (owner 2026-10-10): the line leaves this truck, the
                    // other lines of its ZD keep what they showed.
                    const next = excludeSupplierCargoLine(supplierCargoLines, effectiveSupplierCargoEntries, itemId);
                    if (!next) return false;
                    setSupplierCargoTouched(true);
                    setSupplierCargoEntries(next);
                    // Review 2026-10-12: ZK lines paired with it lose the pair and
                    // their goods from it (no pair to a line this truck does not carry).
                    const pairedKeys = Array.from(cargoPairs.entries())
                      .filter(([, pair]) => pair.lineId === itemId)
                      .map(([rowKey]) => rowKey);
                    if (pairedKeys.length > 0) {
                      setCargoEdited(true);
                      setCargoPairChoices((previous) => ({ ...previous, ...Object.fromEntries(pairedKeys.map((rowKey) => [rowKey, null])) }));
                      const currentItems = (form.getFieldValue('cargoItemsByKey') || {}) as Record<string, CargoItemFormValue>;
                      form.setFieldsValue({
                        cargoItemsByKey: {
                          ...currentItems,
                          ...Object.fromEntries(pairedKeys.map((rowKey) => [rowKey, { ...currentItems[rowKey], quantity: 0 }])),
                        },
                      });
                      pairedKeys.forEach(markQuantityTouched);
                    }
                    return true;
                  }}
                  derivedItemIds={customerSumLineIds}
                />
              </Modal>
            </>
          ) : null}
          selectedSupplierOrderCount={selectedSupplierOrderIds.length}
          canEditTransportCosts={canEditTransportCosts}
          canViewGoodsValue={canViewGoodsValue}
          availableOrders={availableOrders}
          availableColumns={availableColumns}
          orderRowSelection={orderRowSelection}
          scopedOrder={scopedOrder}
          orderScopeActive={Boolean(orderScopeId)}
          canPickOrders={!isEditMode}
          cargoRows={cargoRows}
          allocationHintsLoading={allocationHintsLoading}
          allocationHintsError={allocationHintsError}
          missingCargoOrderNumbers={missingCargoOrderNumbers}
          currency={currency}
          costAllocationMethod={costAllocationMethod}
          showUnitCosts={showUnitCosts}
          onShowUnitCostsChange={setShowUnitCosts}
          allocationItemsById={allocationItemsById}
          formatCurrencyAmount={formatCurrencyAmount}
          onQuantityTouched={markQuantityTouched}
          warehouseAddress={warehouseAddress}
          renderCargoPairing={renderCargoPairing}
          recipientsPanel={dropBoard}
          cargoModalOpen={cargoModalOpen}
          onCargoModalOpenChange={setCargoModalOpen}
          onRemoveCargo={(key) => {
            markQuantityTouched(key);
            const currentItems = (form.getFieldValue('cargoItemsByKey') || {}) as Record<string, CargoItemFormValue>;
            form.setFieldsValue({
              cargoItemsByKey: {
                ...currentItems,
                [key]: {
                  ...currentItems[key],
                  quantity: 0,
                },
              },
            });
            setExcludedCargoKeys((current) => new Set(current).add(key));
          }}
          cargoStatItems={cargoStatItems}
          hasMissingDimensions={hasMissingDimensions}
          deliveryQuantities={status === 'DELIVERED' && selectedCargoItems.length > 0 ? (
            <TransportDeliveryQuantityFields
              cargoItems={selectedCargoItems}
              routeOrders={transport?.routeOrders || []}
            />
          ) : null}
        />
      </div>

      {/* 2. Gdzie jedzie (auto z fabryki): towar → klienci, kolejność rozładunku */}
      {dropBoard ? (
        <TransportFormSection
          sectionKey="destination"
          step={2}
          icon={<AimOutlined />}
          label={t('transport:dropBoard.title')}
          summary={selectedOrders.map((order, index) => `B${index + 1} ${order.customer?.name || order.customerName || ''}`).join(' · ') || undefined}
        >
          {dropBoard}
        </TransportFormSection>
      ) : null}

      {/* 2/3. Trasa i terminy (+ trasa każdego zamówienia) */}
      <TransportRouteSection
        step={dropBoard ? 3 : 2}
        onLoadingTouched={() => setLoadingTouched(true)}
        onDeliveryTouched={() => setDeliveryTouched(true)}
        onEstimatedDeliveryTouched={() => setEstimatedDeliveryTouched(true)}
        estimatedDeliveryFollowsLoading={!isEditMode && !estimatedDeliveryTouched}
        routePoints={<TransportOrderRoutePointsSection form={form} orders={routePointOrders} />}
      />

      {/* 3. Przewoźnik */}
      <TransportCarrierSection
        step={dropBoard ? 4 : 3}
        form={form}
        customerOptions={customerOptions}
        transportCompanyOptions={transportCompanyOptions}
        onCustomerTouched={() => setCustomerTouched(true)}
        isEditMode={isEditMode}
        hideCustomer={Boolean(kind && kindCarriesSupplierOrders(kind))}
      />

      {/* 4. Koszt — permission-gated exactly as before, with the live split */}
      <TransportCostsSection
        step={dropBoard ? 5 : 4}
        canEditTransportCosts={canEditTransportCosts}
        showRateFields
        invoiceRate={transport?.invoice ? {
          number: transport.invoice.number,
          amount: transport.invoice.amount,
          currency: transport.invoice.currency,
        } : null}
        multiOrder={showCustomerSplit}
        allocationPreview={allocationPreview}
        previewColumns={previewColumns}
        currency={currency}
        cargoLineCount={loadedCargoRows.length}
        positionCount={isInbound ? inboundPreviewLines.length : loadedCargoRows.length}
        supplierOrderCurrency={isInbound ? firstSupplierCurrency ?? null : null}
        warnings={transportCostAllocation.warnings}
        renderAllocationWarning={renderAllocationWarning}
        missingDimensionOrders={missingDimensionOrders}
        costAllocationMethod={costAllocationMethod}
        keyAvailability={keyAvailability}
        valueKey={valueKey && !isInbound ? { saved: Boolean(savedValueAllocation) } : null}
        splitPreview={costSplitPreview}
        defaultKeyLabel={kind ? defaultKeyLabel : null}
      />

      </>
      ) : null}

      {/* e. Pojazd i kierowca + Uwagi — optional, collapsed by default (ghost) */}
      {kind ? <TransportVehicleNotesSection step={dropBoard ? 6 : 5} transport={transport} isEditMode={isEditMode} /> : null}

      {/* Sticky live cost summary — permission-gated by canViewTransportCosts. */}
      <TransportStickySummary
        canViewTransportCosts={canViewTransportCosts}
        currency={currency}
        formatCurrencyAmount={formatCurrencyAmount}
        // Round 3 (U19): an unknown (or zero) goods value hides „Wartość
        // towaru” and „Łączny koszt” instead of showing „—”; the total is
        // goods + the cost typed now.
        totalGoodsValue={stickyGoodsValue}
        transportCostForAllocation={transportCostForAllocation}
        totalLandedCost={stickyGoodsValue === null ? null : Math.round((stickyGoodsValue + transportCostForAllocation) * 100) / 100}
        multiOrder={multiOrder && costAllocationMethod !== 'MANUAL'}
        selectedOrderCount={selectedOrders.length}
        submitButtons={stickySubmitButtons}
        missingRequiredFields={missingRequiredFields}
        onRequiredFieldClick={focusRequiredField}
        measuredHeight={stickySummaryHeight}
        onHeightChange={updateStickySummaryHeight}
      />
    </Form>
  );
};

// Decision 2026-10-04: the working sections start open (no hidden fields).
const OPEN_SECTIONS: Array<'orders' | 'destination' | 'route' | 'carrier' | 'costs'> = ['orders', 'destination', 'route', 'carrier', 'costs'];

const SharedTransportForm: React.FC<SharedTransportFormProps> = (props) => (
  <TransportFormSectionsProvider initialOpen={OPEN_SECTIONS}>
    <SharedTransportFormInner {...props} />
  </TransportFormSectionsProvider>
);

export default SharedTransportForm;
